/* Mariposa cart scheduler (BLUEPRINT §3.7). Vanilla JS.
   - Valid pickup dates: next maxDaysAhead days minus closed weekdays (per
     location), blackout dates, and the 2pm-weekday-before cutoff rule.
   - Writes ALL schedule attributes atomically via one /cart/update.js POST
     (canonical keys + legacy Crysalis mirrors, §5.4), on change AND re-asserted
     on checkout click (belt-and-suspenders).
   - Gates checkout (cart page + drawer) client-side when values are missing.
   - Clears stale attributes when the cart no longer holds pickup lines. */
(function () {
  'use strict';

  var DAYNAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  /* Mail Order and Wholesale Shipping have no shopper-chosen date — Eileen asked
     for an ESTIMATED ship date, not a picker (2026-08-20 @ 54:44). Show that
     estimate at the cart, computed with the SAME rule as the checkout Function's
     estimatedShipDate(): today + the longest lead in the cart (min 1), then
     forward past any closed weekday or blackout on this channel. */
  /* OFF THE CART PAGE (the drawer). There is no scheduler config there, so the
     drawer's ship-info block carries its own [data-mariposa-ship-config] with the
     same inputs (snippets/mariposa-ship-info.liquid, drawer: true). It is swapped in
     as cfg for this one synchronous pass, so the drawer is computed by exactly the
     code below — Patti, call 2026-10-08: "make sure that that estimate that you're
     showing on cart page is the same as on the cart drawer". shipOnly stops the pass
     from stamping cart attributes, which stays a cart-page job as before. */
  var shipOnly = false;
  /* Checkout's date format: "Mon, Oct 12" (customerDate() in
     web/app/lib/mail-delivery-window.js — weekday short, month short, day). */
  var DAY_SHORT3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function customerDate(x) { return DAY_SHORT3[x.getDay()] + ', ' + MONTH_LABELS[x.getMonth()] + ' ' + x.getDate(); }
  function readShipOnlyCfg() {
    var n = document.querySelector('[data-mariposa-ship-config]');
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }
  function hideArrive() {
    var a = el('[data-ms-arrive-box]');
    if (a) a.hidden = true;
    var m = el('[data-ms-arrive-more]');
    if (m) m.hidden = true;
  }
  function fillShipEstimate() {
    if (!cfg) {
      var sc = readShipOnlyCfg();
      if (!sc) return;
      var mark = el('[data-ms-ship-estimate]');
      if (mark) mark.setAttribute('data-ms-filled', '1');
      cfg = sc; shipOnly = true;
      try { fillShipEstimate(); } finally { cfg = null; shipOnly = false; }
      return;
    }
    var box = el('[data-ms-ship-estimate]');
    if (!box || !cfg || !cfg.today) return;
    if (cfg.channel !== 'mail' && cfg.channel !== 'ws_ship') {
      box.hidden = true;
      hideArrive();
      return;
    }
    var basis = shipBasis();
    if (basis.err) {
      if (basis.err === 'noapi') {
        try { console.error('mariposa-scheduler: mariposa-ship-estimate.js did not load; ship estimate hidden'); } catch (e) {}
      }
      box.hidden = true;
      hideArrive();
      return;
    }
    var ch = basis.ch, api = basis.api, d = basis.d;
    /* Her arrive-by-Saturday rule needs the destination, because it is what
       decides whether Thursday is offerable at all. transitFor() falls back to
       the national default when the cart has no address — the slowest lane, so
       an unknown destination is never shown a ship day checkout would refuse. */
    var where = api.dest(cfg);
    var transit = api.transitFor(ch && ch.transit_days, where);
    /* Two answers off the same start date. `rolled` is what this page SHOWS —
       destination-aware, because the shopper can see and change the ZIP it used.
       `neutral` is what gets STAMPED onto the cart and the lines, because those
       travel to a checkout that has a different address and no way to recompute.
       See the header of mariposa-ship-estimate.js. */
    var neutral = api.neutralShip(d, ch);
    var rolled = api.rollToShipDay(d, ch, transit && transit.days);
    if (!rolled.date) {
      box.hidden = true;
      hideArrive();
      return;
    }
    d = rolled.date;
    var label = DAYNAMES[d.getDay()] + ', ' + FULL_MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    /* Since 2026-10-09 the block prints checkout's sentence, "Estimated: ships Mon,
       Oct 12 for delivery Wed, Oct 14." (mailEstimateText() in
       web/app/lib/mail-delivery-window.js), so the ship day uses checkout's short
       date. [data-ms-ship-day] is that slot; older markup falls back to the long label. */
    var shipDayEl = box.querySelector('[data-ms-ship-day]');
    var strong = box.querySelector('strong');
    if (shipDayEl) shipDayEl.textContent = customerDate(d);
    else if (strong) strong.textContent = label; else box.textContent = 'Estimated ship date: ' + label;
    box.hidden = false;
    /* The Function checks each line's window for this channel against exactly
       this estimate. Say so here rather than letting the shopper find out at
       checkout (audit 2026-09-01, D8). */
    var warn = el('[data-ms-ship-season]');
    if (warn) warn.hidden = seasonAllows(iso(d));
    fillArrival(d, ch, neutral);
  }

  /* The start of the ship-date computation, shared by fillShipEstimate() above and
     drawerShipValues() below (2026-10-10), so the drawer checkout stamps exactly what
     the cart page would. Reads the current cfg. Returns {ch, api, d} or {err}. */
  function shipBasis() {
    var ch = channelRules();
    /* The roll forward — closed weekdays, blackouts, AND the arrive-by-Saturday
       hold — is MariposaShip.rollToShipDay(), the same function the product page
       uses. It used to be a second copy of the loop here, which is how the cart
       and the PDP could disagree; worse, when the arrival rule was added a
       private copy would have kept offering Thursday to a Brooklyn cart while
       the PDP refused it. If that asset failed to load there is no rule to apply
       and no safe date to show, so the estimate is hidden rather than guessed. */
    var api = window.MariposaShip;
    if (!api || typeof api.rollToShipDay !== 'function') return { err: 'noapi' };
    var closed = (ch && ch.closed_weekdays && ch.closed_weekdays._) || [];
    var black = (ch && ch.blackouts) || [];
    /* NO HARDCODED MINIMUM LEAD (same rule as the 2026-09-08 webhook fix —
       CANON "THE DEFECT"). This read `: 1` until 2026-09-21, which is why a
       wholesale cart at 11am Monday said "Order by 12:00 pm PT today and it
       ships Tuesday" — internally inconsistent, and a day later than her own
       site ships. Her behaviour is lead 0: same-day before the noon cutoff.
       Prep time belongs in per-product lead_days / prep_padding_days, never
       in a literal floor. Guarded by verify-ship-day-rule.cjs check [6]. */
    var lead = cfg.leadDays > 0 ? cfg.leadDays : 0;
    // A1: mail/ws_ship count from this channel's own cutoff (noon), which the
    // cron writes as ch.today; fall back to the global today if absent.
    var d = api.shipBase(ch, cfg.schedule || {}).date;
    d.setDate(d.getDate() + lead);
    /* Store-wide busy-season prep padding, counted in OPEN days — the same rule
       as estimatedShipDate() in the checkout Function and estimateShipDate() in
       the order webhook. The Calendars editor added on 2026-09-04 writes
       prep_padding_days and previewed with it while every enforcing layer
       ignored it, so a padded holiday would have shown three different dates in
       three places. Absent or zero (its value today) this loop does nothing. */
    var pad = ch.prep_padding_days != null ? ch.prep_padding_days : ((cfg.schedule && cfg.schedule.prep_padding_days) || 0);
    var open = function (x) {
      if (black.indexOf(iso(x)) !== -1) return false;
      if ((ch.preparation_dates || []).indexOf(iso(x)) !== -1) return true;
      return ch.preparation_weekdays ? ch.preparation_weekdays.indexOf(x.getDay()) !== -1
        : closed.indexOf(x.getDay()) === -1 || (ch.dispatch_dates || []).indexOf(iso(x)) !== -1;
    };
    for (var padded = 0, g = 0; padded < pad && g < 120; g++) {
      d.setDate(d.getDate() + 1);
      if (open(d)) padded++;
    }
    if (padded < pad) return { err: 'pad' };
    return { ch: ch, api: api, d: d };
  }

  /* MAIL ORDER, DRAWER CHECKOUT (2026-10-10; scratchpad G-drawer-paths.md §3). The three
     ship attributes the confirmation email and the packing slips read (ship_date,
     ship_date_human, arrival_estimate) were only ever written on /cart, by
     stampShipAttributes(), so a shopper who checked out from the drawer — most Mail Order
     shoppers — reached the order without them. This computes the same three values from
     the drawer's [data-mariposa-ship-config], with the same code, for the drawer Checkout
     to write before it lets the submit go. null when there is nothing to compute. */
  function drawerShipValues() {
    var sc = readShipOnlyCfg();
    if (!sc || !sc.today || (sc.channel !== 'mail' && sc.channel !== 'ws_ship')) return null;
    var prevCfg = cfg, prevShipOnly = shipOnly;
    cfg = sc; shipOnly = true;
    try {
      var b = shipBasis();
      if (b.err) return null;
      var neutral = b.api.neutralShip(b.d, b.ch);
      if (!neutral || !neutral.date) return null;
      return {
        ship_date: iso(neutral.date),
        ship_date_human: neutral.text,
        arrival_estimate: b.api.rangeWords(b.api.nationalDays(b.ch))
      };
    } catch (e) {
      return null;
    } finally {
      cfg = prevCfg; shipOnly = prevShipOnly;
    }
  }

  /* A8(a) + A8(b). The ship date above is only half of what a Mail Order
     shopper asks. The other half — when it lands, and what the noon cutoff
     means right now — is computed by the shared module in
     assets/mariposa-ship-estimate.js so the product page and the cart cannot
     drift apart, and so the transit table stays in the schedule metafield
     rather than in two copies of theme JS.

     Degrades silently if that asset failed to load: the ship-date line, which
     is the pre-existing behaviour, still renders. */
  function fillArrival(shipDate, ch, neutral) {
    var api = window.MariposaShip;
    var boxA = el('[data-ms-arrive-box]');
    if (!api || !boxA) return;

    var where = api.dest(cfg);
    var t = api.transitFor(ch && ch.transit_days, where);

    /* Since 2026-10-09 the arrival LINE sits in the always-visible lead and the ZIP
       editor, cutoff and transit note sit in the collapsible "Shipping details", so
       the lookups are scoped to the whole ship-info block, not the arrival box. */
    var scope = (boxA.closest && boxA.closest('[data-ms-block="ship-info"]')) || boxA;
    var moreBox = scope.querySelector('[data-ms-arrive-more]');
    var arriveEl = scope.querySelector('[data-ms-arrive]');
    var destEl = scope.querySelector('[data-ms-arrive-dest]');
    var sentence = scope.querySelector('[data-ms-ship-estimate] [data-ms-ship-day]');
    if (sentence && arriveEl) {
      /* Checkout's sentence needs real dates, so the window is always computed: the
         shopper's ZIP zone when one is known, otherwise the schedule's normal
         (default) transit days — the Flat Rate window checkout prints. No transit
         table at all means no honest sentence, so the line hides. */
      var shipBox = scope.querySelector('[data-ms-ship-estimate]');
      if (!t) { if (shipBox) shipBox.hidden = true; }
      else {
        var win = api.arrival(shipDate, t.days, ch);
        arriveEl.textContent = customerDate(win.min) + (win.same ? '' : '\u2013' + customerDate(win.max));
        /* Drawer card subtitle (Tom's mockup 2026-10-09): "2–3 day delivery", the day
           count of this same window; a single number when transit is one value. */
        var daysEl = scope.querySelector('[data-ms-transit-days]');
        if (daysEl) daysEl.textContent = t.days[0] === t.days[1] ? String(t.days[0]) : t.days[0] + '\u2013' + t.days[1];
      }
    } else if (arriveEl) {
      if (!t) {
        arriveEl.textContent = '';
      } else if (where.source === 'none') {
        /* No address anywhere on the page. Say the range in days rather than
           inventing dates for a destination we do not have. */
        arriveEl.textContent = t.days[0] === t.days[1]
          ? t.days[0] + ' day' + (t.days[0] === 1 ? '' : 's') + ' after it ships'
          : t.days[0] + '–' + t.days[1] + ' days after it ships';
      } else {
        arriveEl.textContent = api.fmtArrival(api.arrival(shipDate, t.days, ch));
      }
    }
    if (destEl) destEl.textContent = t ? api.destLabel(where, t) : '';

    var editEl = scope.querySelector('[data-mship-dest-edit]');
    if (editEl) editEl.textContent = where.source === 'none' ? 'Estimate for my ZIP code' : 'Change ZIP';

    var cutEl = scope.querySelector('[data-ms-cutoff]');
    if (cutEl) {
      var note = api.cutoffNote(ch, cfg.schedule || {}, iso(shipDate), t && t.days);
      cutEl.textContent = note.text;
      cutEl.hidden = false;
    }

    var noteEl = scope.querySelector('[data-ms-transit-note]');
    /* A saved group order ships to the recipients, not to the checkout address;
       the shared script carries the group wording (2026-09-10). */
    var noteBox = document.querySelector('[data-ms-arrive-box]');
    var isGroup = !!(noteBox && noteBox.getAttribute('data-mship-group') === '1' && api.groupNote);
    /* When Eileen has written the merged paragraph (channels.<ch>.cart_note) it is
       the whole wording, so this line would repeat half of it. The GROUP note is
       not part of what she merged — it says each recipient is shipped to
       separately — so that one still shows. */
    var merged = !isGroup && !!document.querySelector('[data-ms-cart-note]');
    var noteTxt = isGroup ? api.groupNote(where, t)
      : (merged ? '' : api.estimateNote(where, t, cfg.channel));
    if (noteEl && noteTxt) { noteEl.textContent = noteTxt; noteEl.hidden = false; }

    api.wireDestControls(scope, function () { fillShipEstimate(); });
    boxA.hidden = false;
    if (moreBox) moreBox.hidden = false;

    stampShipAttributes(neutral, ch);
  }

  /* A8(d). The order confirmation email is a Shopify notification template, and
     notification Liquid can read cart ATTRIBUTES (that is how the pickup block
     added on 2026-08-14 works — it keys on attributes.pickup_date) but it cannot
     read the schedule metafield or run any of the arithmetic above. So the
     estimate the shopper was shown is written onto the cart, and the email
     quotes it back rather than recomputing a second, possibly different answer.

     Written on the Mail Order AND Wholesale Shipping paths, once, and only when
     it has actually changed — a blind POST on every render would fight the
     cart's own refresh loop.

     ws_ship added 2026-09-22: fillShipEstimate already computes and SHOWS the
     estimate for ws_ship (see its channel gate), but this stamp was mail-only,
     so a wholesale-shipping order reached ShipStation carrying shopping_mode +
     ws_fulfillment and NO ship_date — the native import had no ship-by date to
     read. The value stamped is `neutral` (destination-independent) exactly as
     for mail; the order webhook reconciles it against the real address. */
  var shipStampInFlight = false;
  function stampShipAttributes(neutral, ch) {
    if (shipOnly) return;
    if (cfg.channel !== 'mail' && cfg.channel !== 'ws_ship') return;
    if (shipStampInFlight) return;
    var api = window.MariposaShip;
    if (!api || !neutral || !neutral.date) return;

    /* DESTINATION-INDEPENDENT BY CONSTRUCTION. These three values leave the cart
       and are re-read at a checkout that knows an address this page never saw,
       so nothing computed from the estimator's ZIP may appear in them.
       `neutral.text` is the exact ship date when every destination shares it and
       "by <date>" when a faster zone would go sooner; the arrival is the national
       range in words, the slowest lane, which no destination can be later than.
       Measured defect this replaces: a cart estimating for ZIP 94609 stamped
       "Estimated arrival: Wed, Sep 9 - Thu, Sep 10" onto lines that then rendered
       unchanged at a Brooklyn checkout (2026-09-06). */
    var shipIso = iso(neutral.date);
    var human = neutral.text;
    var arriveTxt = api.rangeWords(api.nationalDays(ch));

    var attrsFresh = cfg.cartShipDate === shipIso && cfg.cartArrival === arriveTxt;
    if (attrsFresh) {
      /* The attributes are already right, but a line ADDED since they were
         written still has no stamp on it — which is why one mail line showed
         Ships/arrival and the next showed neither (browser retest 2026-09-06:
         Mariposa Sampler stamped, Biscotti Trio Gift Set added afterwards and
         left blank). This return used to happen before stampMailLines() was ever
         reached. The line pass is cheap and does nothing when nothing is
         missing, so run it every time. (Since 2026-09-21 the pass only SCRUBS
         old per-line stamps — see stampMailLines.) */
      stampMailLines();
      return;
    }

    shipStampInFlight = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          ship_date: shipIso,
          ship_date_human: human,
          arrival_estimate: arriveTxt,
        },
      }),
    }).then(function () {
      cfg.cartShipDate = shipIso;
      cfg.cartArrival = arriveTxt;
      shipStampInFlight = false;
      return stampMailLines();
    }).catch(function () { shipStampInFlight = false; });
  }

  /* THE PER-LINE "Ships:" / "Estimated arrival:" STAMPS ARE REMOVED (2026-09-21).
     They existed for Eileen's 9/5 deck note ("Mail Order checkout does not give
     estimated ship day or arrival day") — line properties render in the checkout
     order summary where cart attributes do not. But line properties ALSO travel
     onto the order and into ShipStation, where they append to the item
     description and pollute her pick lists — Eileen, 9/21, order 1073 ("Ships:
     Monday, September 21, 2026 Estimated arrival: 2–3 days after it ships" on a
     Pecan Pie pick line). The estimate she asked for lives where she asked for
     it: the cart attributes written above (ship_date / ship_date_human /
     arrival_estimate), which reach the order's Additional details, the admin and
     the confirmation email. The pick list is staff-facing and daily; the
     checkout-summary date was a nice-to-have. TRADE-OFF ON RECORD: removing the
     stamps re-opens the 9/5 note for the checkout page itself — if she wants the
     date back at checkout without touching ShipStation, the paths are the
     Delivery Promises API (scopes not held; ACCESS_DENIED, introspected
     2026-09-06) or a checkout UI extension.

     WHAT REMAINS BELOW is the SCRUB: any cart stamped before this change still
     carries the two keys and would land them on the next order, so the pass now
     only strips `Ships` / `Estimated arrival` (and, as before, never lets a
     Will Call/Pickup stamp survive on a Mail Order line). The Will Call and
     Pickup stamps on their own channels are untouched — staff depend on them.

     KEYED BY LINE KEY, NOT INDEX. `item.key` names the row; `line: i + 1` names
     its position, which a concurrent add or remove between the snapshot and the
     write would invalidate. (A properties change does NOT itself reorder the
     cart — measured 2026-09-06 — so this is precision, not a bug fix.) */
  var mailLineStampInFlight = false;
  var MAIL_SHIP_KEY = 'Ships';
  var MAIL_ARRIVE_KEY = 'Estimated arrival';

  /* NO CROSS-RENDER CACHE. This used to short-circuit on a `lastMailLineStamp`
     key of the two strings, so once a cart had been stamped, a line ADDED
     afterwards was never looked at again — the missing-block half of finding 12.
     The per-line test below already makes a repeat pass free apart from one
     /cart.js read, and the in-flight flag still prevents overlap.

     SCRUB ONLY since 2026-09-21: no arguments, writes nothing new. */
  function stampMailLines() {
    if (!cfg || cfg.channel !== 'mail') return Promise.resolve();
    if (mailLineStampInFlight) return Promise.resolve();
    mailLineStampInFlight = true;
    return mailLinePass(0)
      .catch(function () {})
      .then(function () { mailLineStampInFlight = false; });
  }

  /* Same defect, same fix as applyStamp (A9): these writes used to be
     fire-and-forget, so a 429 or a stale key left a Mail Order line carrying an
     out-of-date property with nothing anywhere saying so. Read the status, then
     verify by re-reading the cart, and retry what is still wrong against fresh
     keys. */
  function mailLinePass(attempt) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Every line still carrying a NON-EMPTY ship-estimate stamp, shipped or
           not, fee or not — the keys must not reach the order from anywhere.
           Empty-string values are the fallback end state below and are already
           invisible, so they never count as work to do (or the verify pass
           would retry forever on a line the add-path could not rebuild). */
        var items = (cart.items || []).filter(function (it) {
          var p = it.properties || {};
          return !!(p[MAIL_SHIP_KEY] || p[MAIL_ARRIVE_KEY]);
        });
        if (!items.length) return attempt ? refreshCartSection() : null;
        if (attempt >= 3) return refreshCartSection();

        var chain = Promise.resolve();
        var refused = 0;
        items.forEach(function (item) {
          var props = {};
          Object.keys(item.properties || {}).forEach(function (k) {
            /* Never carry a pickup/Will Call stamp onto a Mail Order line: a cart
               that switched path would otherwise show both. */
            if (k !== 'Will Call' && k !== 'Pickup' && k !== MAIL_SHIP_KEY && k !== MAIL_ARRIVE_KEY) {
              props[k] = item.properties[k];
            }
          });
          chain = chain.then(function () {
            if (Object.keys(props).length) {
              /* A non-empty properties object REPLACES the line's properties
                 wholesale — measured live 2026-09-21. */
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // quantity is mandatory here: /cart/change.js resets a re-keyed line
                // to 1 when it is omitted, which silently breaks a min-qty line.
                body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props })
              }).then(function (r) { if (!r.ok) refused += 1; })
                .catch(function () { refused += 1; });
            }
            /* The stamps were the line's ONLY properties. /cart/change.js
               IGNORES an empty (or null) properties object — measured live
               2026-09-21: HTTP 200, properties untouched — so "replace with
               nothing" cannot be said in one call. Rebuild instead: add a clean
               line for the same variant FIRST (so a failed add loses nothing),
               then remove the stamped line. If the add is refused (inventory
               cap while both lines coexist), fall back to blanking the values
               in place — an empty-valued property renders nowhere. */
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: item.id, quantity: item.quantity })
            }).then(function (r) {
              if (r.ok) {
                return fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: item.key, quantity: 0 })
                }).then(function (r2) { if (!r2.ok) refused += 1; });
              }
              var blank = {};
              blank[MAIL_SHIP_KEY] = '';
              blank[MAIL_ARRIVE_KEY] = '';
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: blank })
              }).then(function (r3) { if (!r3.ok) refused += 1; });
            }).catch(function () { refused += 1; });
          });
        });
        return chain.then(function () {
          var wait = refused ? 500 * (attempt + 1) : 0;
          return new Promise(function (res) { setTimeout(res, wait); })
            .then(function () { return mailLinePass(attempt + 1); });
        });
      });
  }
  var MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var cfg = null;
  var writing = false;
  var drawerChecking = false;
  var calMonth = null; // first-of-month currently displayed in the calendar grid

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function iso(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }

  /* The 2pm cutoff is a bakery-clock rule: evaluate it in America/Los_Angeles,
     not the buyer's zone. At 12:30pm PT on 2026-08-27 a Central-timezone buyer
     lost next-day because their local clock was past 2 — the server minDate
     floors the calendar but never widens it, so the client clock must not
     narrow it either. Falls back to the local clock only when the runtime
     lacks IANA zone support. */
  function nowLA() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', hourCycle: 'h23'
      }).formatToParts(new Date());
      var o = {};
      parts.forEach(function (x) { o[x.type] = x.value; });
      var d = new Date(+o.year, +o.month - 1, +o.day);
      d.laHour = +o.hour;
      return d;
    } catch (e) {
      var n = new Date();
      var d2 = new Date(n.getFullYear(), n.getMonth(), n.getDate());
      d2.laHour = n.getHours();
      return d2;
    }
  }

  /* Earliest valid date per the live pick-up-information rule:
     orders by 2pm Mon–Fri for next-day. If now is a weekday before the cutoff,
     earliest = tomorrow; otherwise earliest = the day after the next weekday. */
  /* This channel's cutoff hour, falling back to the store-wide one. Pickup and
     Will Call use the 2pm global; Mail Order and Wholesale Shipping cut off at
     noon, and their noon is applied server-side by the cron (channels.<ch>.today
     — see fillShipEstimate above), because neither has a picker. Reading the
     per-channel key here means a cutoff Eileen changes on one calendar moves
     that calendar's floor and no other. `channels.<ch>.cutoff.hour` is the same
     key web/cron/pickup-window.mjs channelCutoffHour() reads. */
  function cutoffHourFor() {
    var ch = channelRules();
    if (ch && ch.cutoff && typeof ch.cutoff.hour === 'number') return ch.cutoff.hour;
    return cfg.cutoffHour;
  }

  function earliestDate(now) {
    var day = now.getDay();
    var isWeekday = day >= 1 && day <= 5;
    var d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (isWeekday && now.laHour < cutoffHourFor()) {
      /* Will Call collects SAME DAY before its cutoff (Eileen: order before
         noon, collect that day). Retail pickup keeps next-day. The Function's
         ws_willcall floor is ch.today, which agrees with this. */
      if (cfg.channel !== 'ws_willcall') d.setDate(d.getDate() + 1);
      return d;
    }
    // find the next weekday strictly after today; retail pickup needs the day
    // AFTER it (a full production day), but Will Call collects the very next
    // business day (Eileen, report 911e149e: Friday 10:39pm -> expected
    // "next available Will Call would be Monday", not Tuesday).
    do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
    if (cfg.channel !== 'ws_willcall') d.setDate(d.getDate() + 1);
    return d;
  }

  function parseIso(s) {
    var p = s.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  /* The channel's own rules out of the bundled `mariposa.schedule` metafield —
     the SAME object the checkout Function reads. Before this the calendar read
     the single shared shop-level blackout list while checkout enforced the
     per-channel one, so the moment anyone used the per-channel feature (which
     Eileen asked for five times on the 2026-08-20 call) the calendar would offer
     a date checkout rejects (audit 2026-08-21, finding 21). Falls back to the
     shared list, so a store that has not split anything behaves as before. */
  function channelRules() {
    var sc = cfg.schedule && cfg.schedule.channels;
    if (!sc || !cfg.channel) return null;
    return sc[cfg.channel] || null;
  }

  /* Per-product seasonal windows. Same rule as dateAllowed() in
     extensions/schedule-validation/src/cart_validations_generate_run.js: an
     empty or missing list means always available; {from,to} is inclusive; a
     bare {from} means "from then on"; a plain string is a single date. */
  function availAllows(list, isoDate) {
    if (!Array.isArray(list) || list.length === 0) return true;
    var wd = parseIso(isoDate).getDay();
    for (var i = 0; i < list.length; i++) {
      var w = list[i];
      if (typeof w === 'string') { if (w === isoDate) return true; continue; }
      if (!w) continue;
      /* Bake schedule: {"weekdays":[5,6]} is "Fridays and Saturdays", 0=Sunday.
         Same rule as dateAllowed() in the checkout Function. */
      if (Array.isArray(w.weekdays) && w.weekdays.indexOf(wd) === -1) continue;
      if (w.from && w.to) { if (isoDate >= w.from && isoDate <= w.to) return true; continue; }
      if (w.from && !w.to) { if (isoDate >= w.from) return true; continue; }
      if (Array.isArray(w.weekdays)) return true;
    }
    return false;
  }

  /* Every line in the cart has to allow the date, for THIS channel — the same
     loop the Function runs. cfg.lineAvail carries one availability object per
     cart line that has one; a line with none imposes nothing. */
  function seasonAllows(isoDate) {
    var lines = (cfg && cfg.lineAvail) || [];
    if (!lines.length || !cfg.channel) return true;
    for (var i = 0; i < lines.length; i++) {
      if (!availAllows(lines[i] && lines[i][cfg.channel], isoDate)) return false;
    }
    return true;
  }

  /* The soonest date any cart line's season opens, for the empty-calendar
     message. Empty string when there is nothing to name. */
  function seasonNextFrom() {
    var lines = (cfg && cfg.lineAvail) || [];
    var today = (cfg && cfg.today) || '';
    var best = '';
    for (var i = 0; i < lines.length; i++) {
      var list = lines[i] && lines[i][cfg.channel];
      if (!Array.isArray(list)) continue;
      for (var j = 0; j < list.length; j++) {
        var w = list[j];
        var f = typeof w === 'string' ? w : (w && w.from);
        if (!f || f <= today) continue;
        if (best === '' || f < best) best = f;
      }
    }
    return best;
  }

  /* Why the calendar came back empty, said in a way the shopper can act on.
     Returns '' when seasonality is not the cause, so the generic line stands. */
  function seasonEmptyMessage() {
    var lines = (cfg && cfg.lineAvail) || [];
    var restricted = false;
    for (var i = 0; i < lines.length; i++) {
      var list = lines[i] && lines[i][cfg.channel];
      if (Array.isArray(list) && list.length) { restricted = true; break; }
    }
    if (!restricted) return '';
    var word = cfg.b2b ? 'Will Call' : 'pickup';
    var from = seasonNextFrom();
    if (from) {
      var d = parseIso(from);
      return 'An item in your cart is only sold from ' + FULL_MONTHS[d.getMonth()] + ' ' + d.getDate() +
        '. Remove it to book a ' + word + ' date now.';
    }
    return 'An item in your cart is not sold on any of the dates we are open for ' + word +
      '. Please remove it to book a ' + word + ' date.';
  }

  function validDates(locationName) {
    var loc = cfg.locations[locationName];
    if (!loc) return [];
    var ch = channelRules();
    var closed = loc.closedWeekdays || [];
    if (ch && ch.closed_weekdays) {
      var cw = ch.closed_weekdays[locationName];
      if (cw === undefined) cw = ch.closed_weekdays._;
      if (Array.isArray(cw)) closed = cw;
    }
    var blackout = (ch && Array.isArray(ch.blackouts)) ? ch.blackouts : (cfg.blackoutDates || []);
    var now = nowLA();
    var start = earliestDate(now);
    // authoritative floor: the cron-maintained, LA-timezone booking window —
    // the customer's local clock can't open days the server would reject.
    // Will Call's floor is its channel `today` (same-day before noon), not the
    // global next-day pickup window — matching the Function's ws_willcall floor.
    if (cfg.minDate) {
      var srvMin = parseIso(cfg.minDate);
      if (cfg.channel === 'ws_willcall' && ch && ch.today) srvMin = parseIso(ch.today);
      if (srvMin > start) start = srvMin;
    }
    // lead time: an item in the cart needs N days' notice — earliest is
    // whichever is later, the cutoff-rule date or today + N days
    if (cfg.leadDays > 0 && cfg.today) {
      /* Count from the later of the rendered `today` and the LA clock's date. A cart page
         left open over midnight kept yesterday's `today`, so with a 2+ day lead it offered
         a date the drawer (rendered fresh) and the checkout Function refuse (W-drawer-date.md). */
      var leadStart = parseIso(cfg.today);
      var clockDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (clockDay > leadStart) leadStart = clockDay;
      leadStart.setDate(leadStart.getDate() + cfg.leadDays);
      if (leadStart > start) start = leadStart;
    }
    // capacity: grey out days that hit the location's daily cap (caps default
    // {} — dormant until the merchant sets one in the admin app)
    var cap = (ch && ch.caps && ch.caps[locationName] != null) ? ch.caps[locationName] : (cfg.dailyCaps || {})[locationName];
    var counts = (cfg.dayCounts || {})[locationName] || {};
    var horizon = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    horizon.setDate(horizon.getDate() + cfg.maxDaysAhead);
    if (cfg.maxDate) {
      var srvMax = parseIso(cfg.maxDate);
      if (srvMax < horizon) horizon = srvMax;
    }
    /* A SHIPPING channel's calendar is a list of ship days, so the
       arrive-by-Saturday rule applies to every entry in it exactly as it does to
       the estimate. Pickup and Will Call are collection dates and the rule says
       nothing about them, so `shipRule` is null there and the loop is unchanged.
       Mail Order has no picker today — the estimate above is what it shows — but
       this floor exists so that the day one is switched on it cannot offer a
       Thursday to Brooklyn that checkout would then refuse. */
    var api = window.MariposaShip;
    var shipRule = null;
    if ((cfg.channel === 'mail' || cfg.channel === 'ws_ship') && api && ch) {
      var shipTransit = api.transitFor(ch.transit_days, api.dest(cfg));
      shipRule = shipTransit ? shipTransit.days : null;
    }

    var out = [];
    for (var d = new Date(start); d <= horizon; d.setDate(d.getDate() + 1)) {
      if (closed.indexOf(d.getDay()) !== -1) continue;
      if (blackout.indexOf(iso(d)) !== -1) continue;
      if (shipRule && !api.shipDayAllowed(d, ch, shipRule)) continue;
      if (cap > 0 && (counts[iso(d)] || 0) >= cap) continue;
      /* A seasonal product's own window. Without this the calendar offered
         dates the checkout Function then refused with "An item in your cart is
         not available on that date", and when no offered date fell inside the
         window there was no date the shopper could pick at all
         (audit 2026-09-01, D1). */
      if (!seasonAllows(iso(d))) continue;
      out.push(new Date(d));
    }
    return out;
  }

  function timeLabel(hhmm) {
    var parts = hhmm.split(':');
    var h = parseInt(parts[0], 10);
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + ':' + parts[1] + ' ' + ampm;
  }

  function dateLabel(d) {
    return DAYNAMES[d.getDay()].slice(0, 3) + ', ' + MONTH_LABELS[d.getMonth()] + ' ' + d.getDate();
  }

  /* Legacy Crysalis mirror: "08/11/2026 Tuesday 10:00 am" (PLAN.md good order) */
  function legacyDeliveryDate(isoDate, hhmm) {
    var p = isoDate.split('-');
    var d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    var tp = hhmm.split(':');
    var h = parseInt(tp[0], 10);
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    var hh = h12 < 10 ? '0' + h12 : '' + h12;
    var day = parseInt(p[2], 10);
    /* exact old-site format: mm/d/yyyy DDDD hh:mm ampm (unpadded day, padded hour) */
    return p[1] + '/' + day + '/' + p[0] + ' ' + DAYNAMES[d.getDay()] + ' ' + hh + ':' + tp[1] + ' ' + ampm;
  }

  function el(sel) { return document.querySelector(sel); }

  function populateDates(keepValue) {
    var locSel = el('[data-ms-location]');
    var dateSel = el('[data-ms-date]');
    if (!locSel || !dateSel) return;
    var dates = validDates(locSel.value);
    dateSel.innerHTML = '<option value="">Choose a date…</option>' + dates.map(function (d) {
      return '<option value="' + iso(d) + '">' + dateLabel(d) + '</option>';
    }).join('');
    if (keepValue && dates.some(function (d) { return iso(d) === keepValue; })) dateSel.value = keepValue;
  }

  /* The time slots offered for a location on a date, with no DOM side effects.
     Extracted from populateTimes() on 2026-10-10 so the express-checkout gate and the
     cart drawer can check a chosen time without rebuilding the <select> (rebuilding it
     every 30 s would close a dropdown the shopper has open). populateTimes() renders
     exactly this list. dateIso '' gives the location's base slots, as before. */
  function timeSlotsFor(locName, dateIso) {
    var loc = cfg.locations[locName] || { timeSlots: [] };
    var slots = loc.timeSlots || [];
    /* The Function checks the chosen time against ch.hours[location][weekday].
       Read the same per-weekday list here so a slot is never offered that
       checkout will reject (Will Call is 10-3 Mon-Fri, Oakland is not). */
    var ch = channelRules();
    if (ch && ch.hours && ch.hours[locName] && dateIso) {
      var wd = parseIso(dateIso).getDay();
      var byDay = ch.hours[locName][String(wd)];
      if (Array.isArray(byDay)) slots = byDay;
    }
    /* Same-day Will Call: a slot already in the past cannot be collected.
       Offer only slots after the current LA hour when the chosen date is
       today. (The exact preparation buffer is an open client question; until
       it is answered, "later than now" is the floor.) */
    if (dateIso) {
      var nowSD = nowLA();
      if (dateIso === iso(nowSD)) {
        var parts = new Intl.DateTimeFormat('en-US',{timeZone:'America/Los_Angeles',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(new Date());
        var clock={};parts.forEach(function(p){clock[p.type]=p.value;});
        var earliest=Number(clock.hour)*60+Number(clock.minute)+Number(clock.second)/60+120;
        slots=slots.filter(function(t){return Number(t.slice(0,2))*60+Number(t.slice(3,5))>=earliest;});
      }
    }
    return slots;
  }

  function populateTimes(keepValue) {
    var locSel = el('[data-ms-location]');
    var timeSel = el('[data-ms-time]');
    var dateSel = el('[data-ms-date]');
    if (!locSel || !timeSel) return;
    var slots = timeSlotsFor(locSel.value, dateSel ? dateSel.value : '');
    timeSel.innerHTML = '<option value="">Choose a time…</option>' + slots.map(function (t) {
      return '<option value="' + t + '">' + timeLabel(t) + '</option>';
    }).join('');
    if (keepValue && slots.indexOf(keepValue) !== -1) timeSel.value = keepValue;
    /* D-024 (Eileen, 2026-09-24): "Make 10:00 am the default for time".
       Applied ONLY if 10:00 survives the availability filters above — location
       weekday hours, and on a same-day pickup the "later than now + 120min"
       floor. If 10:00 is not offered, the empty "Choose a time…" option stays
       selected exactly as before, so nothing is bypassed: the shopper is still
       blocked until they pick a real slot. Never overrides a value the shopper
       already has (keepValue wins). */
    else if (slots.indexOf('10:00') !== -1) timeSel.value = '10:00';
  }

  /* ---- Calendar grid (visual layer over the same validDates list) ---- */
  function renderCalendar() {
    var cal = el('[data-ms-calendar]');
    var locSel = el('[data-ms-location]');
    var dateSel = el('[data-ms-date]');
    if (!cal || !locSel || !dateSel) return;
    var dates = validDates(locSel.value);
    if (!dates.length) {
      var why = seasonEmptyMessage();
      cal.innerHTML = '<p class="ms-hint">' + (why || 'No pickup dates available right now.') + '</p>';
      return;
    }
    var valid = {};
    dates.forEach(function (d) { valid[iso(d)] = true; });
    var minMonth = new Date(dates[0].getFullYear(), dates[0].getMonth(), 1);
    // Browsing months is separate from choosing an eligible date. A seasonal
    // item must not make the next-month arrow appear broken (report 7d80d09e).
    // Keep date eligibility in validDates(); browse only the normal booking window.
    var now = nowLA();
    var last = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    last.setDate(last.getDate() + cfg.maxDaysAhead);
    if (cfg.maxDate && parseIso(cfg.maxDate) < last) last = parseIso(cfg.maxDate);
    var maxMonth = new Date(last.getFullYear(), last.getMonth(), 1);
    if (!calMonth || calMonth < minMonth || calMonth > maxMonth) calMonth = new Date(minMonth);

    var y = calMonth.getFullYear(), m = calMonth.getMonth();
    var daysInMonth = new Date(y, m + 1, 0).getDate();
    var firstDow = new Date(y, m, 1).getDay();
    var selected = dateSel.value;

    var html = '<div class="ms-cal-head">';
    html += '<button type="button" class="ms-cal-nav" data-ms-cal-prev aria-label="Previous month"' +
      (calMonth <= minMonth ? ' disabled' : '') + '>&larr;</button>';
    html += '<span class="ms-cal-title">' + MONTHS_FULL[m] + ' ' + y + '</span>';
    html += '<button type="button" class="ms-cal-nav" data-ms-cal-next aria-label="Next month"' +
      (calMonth >= maxMonth ? ' disabled' : '') + '>&rarr;</button>';
    html += '</div><div class="ms-cal-grid">';
    ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].forEach(function (w) { html += '<span class="ms-cal-dow">' + w + '</span>'; });
    for (var b = 0; b < firstDow; b++) html += '<span class="ms-cal-blank"></span>';
    for (var day = 1; day <= daysInMonth; day++) {
      var dIso = y + '-' + pad(m + 1) + '-' + pad(day);
      if (valid[dIso]) {
        html += '<button type="button" class="ms-cal-day' + (dIso === selected ? ' ms-cal-day--selected' : '') +
          '" data-ms-day="' + dIso + '" aria-pressed="' + (dIso === selected ? 'true' : 'false') + '">' + day + '</button>';
      } else {
        html += '<span class="ms-cal-day ms-cal-day--off" aria-hidden="true">' + day + '</span>';
      }
    }
    html += '</div>';
    cal.innerHTML = html;
    var shownDates = dates.filter(function (d) { return d.getFullYear() === y && d.getMonth() === m; });
    if (!shownDates.length) {
      var limiting = [];
      ((cfg && cfg.lineAvail) || []).forEach(function (availability, index) {
        var windows = availability && availability[cfg.channel];
        if (!Array.isArray(windows) || !windows.length) return;
        var allowed = false;
        for (var n = 1; n <= daysInMonth; n++) {
          if (availAllows(windows, y + '-' + pad(m + 1) + '-' + pad(n))) { allowed = true; break; }
        }
        if (!allowed) limiting.push((cfg.lineAvailTitles || [])[index] || 'A seasonal item');
      });
      var hint = document.createElement('p');
      hint.className = 'ms-hint';
      hint.setAttribute('data-ms-month-unavailable', '');
      hint.setAttribute('role', 'status');
      hint.textContent = limiting.length
        ? 'No available dates in ' + MONTHS_FULL[m] + ' for this cart. ' + limiting.join(', ') +
          ' is not available that month. Choose an available date in another month, or remove the seasonal item to see more dates.'
        : 'No available dates in ' + MONTHS_FULL[m] + ' for this cart. Please choose an available date in another month.';
      cal.appendChild(hint);
    }
  }


  /* ---- Fresh or frozen on the chosen date -------------------------------
     Mariposa publishes a bake schedule (pickup.mariposabaking.com/pages/
     bread-schedule). Its own preamble is what makes this a NOTE and not a
     rule: "If it is ordered on a different day, we will hold a frozen bread
     for you instead." So the bake day never removes a date from the calendar
     — it only decides what the shopper collects. Deliberately kept out of
     cfg.lineAvail, which DOES remove dates.

     cfg.bakeDayTitles[i] / cfg.bakeDayDays[i] are parallel arrays, one entry
     per cart line that carries mariposa.bread_schedule_days. Day numbers are
     0 = Sunday, the same convention as weekdayOf() in the checkout Function. */
  function renderFreshNote() {
    var box = el('[data-ms-fresh-note]');
    if (!box) return;
    var titles = (cfg && cfg.bakeDayTitles) || [];
    var days = (cfg && cfg.bakeDayDays) || [];
    var dateSel = el('[data-ms-date]');
    var picked = dateSel ? dateSel.value : '';
    if (!titles.length || !picked) { box.hidden = true; box.innerHTML = ''; return; }
    var wd = parseIso(picked).getDay();
    var fresh = [], frozen = [];
    for (var i = 0; i < titles.length; i++) {
      var list = (days[i] || []).map(Number);
      (list.indexOf(wd) === -1 ? frozen : fresh).push(titles[i]);
    }
    var html = '<p class="ms-fresh-note__head">Fresh or frozen on ' + readableDate(picked) + '</p>';
    if (fresh.length) html += '<p class="ms-hint">Baked fresh that day: ' + fresh.join(', ') + '.</p>';
    if (frozen.length) html += '<p class="ms-hint">Frozen that day: ' + frozen.join(', ') + '.</p>';
    if (frozen.length) html += '<p class="ms-hint">Every bread is on the menu every pickup day.</p>';
    box.innerHTML = html;
    box.hidden = false;
  }
  function currentValues() {
    var locSel = el('[data-ms-location]');
    var dateSel = el('[data-ms-date]');
    var timeSel = el('[data-ms-time]');
    return {
      location: locSel ? locSel.value : '',
      date: dateSel ? dateSel.value : '',
      time: timeSel ? timeSel.value : ''
    };
  }

  function pickupComplete() {
    if (!cfg || !cfg.hasPickupLines) return true;
    var v = currentValues();
    if (!(v.location && v.date && v.time)) return false;
    if (!validDates(v.location).some(function (d) { return iso(d) === v.date; })) return false;
    // Rebuild the time choices at checkout as a tab may have stayed open past cutoff.
    populateTimes(v.time);
    return currentValues().time === v.time;
  }

  function writeAttributes() {
    var v = currentValues();
    var attrs;
    if (cfg.hasPickupLines && v.location && v.date && v.time) {
      attrs = {
        pickup_date: v.date,
        pickup_time: v.time,
        pickup_location: v.location,
        delivery_date: legacyDeliveryDate(v.date, v.time),
        'pick-up-location': v.location
      };
      /* Wholesale Will Call is the DEFAULT when the buyer never touches the radio, and
         nothing wrote it in that case, so the packing slip (print-slips.js reads
         ws_fulfillment === 'willcall') printed "Shipping" on a Will Call order. Write the
         effective choice with the schedule (2026-10-10, H-wholesale-verify.md change 2). */
      if (cfg.b2b && cfg.channel === 'ws_willcall') attrs.ws_fulfillment = 'willcall';
    } else if (!cfg.hasPickupLines) {
      // stale-attribute clearing (edge case 2)
      attrs = { pickup_date: '', pickup_time: '', pickup_location: '', delivery_date: '', 'pick-up-location': '' };
    } else {
      return Promise.resolve();
    }
    writing = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      writing = false;
      updateDrawerSummary();
      /* RETURN the stamp chain. It used to be fired and dropped, so
         writeAttributes() resolved while its own line writes were still in
         flight — which is how the checkout gate's
         writeAttributes().then(restampFromCart) came to run two overlapping
         chains. Callers that await this now await the lines too. */
      /* Not on a wholesale cart that is not Will Call: there the strip
         (clearWillCallStamps) is the ONLY line writer. An empty-value stamp run here
         rewrote the same stamped lines the page-load strip was rebuilding, and a
         reload mid-switch to Delivery doubled, lost or left a stamped line (R-fixbatch
         R1). The attribute-only clear above still runs. */
      if (cfg.b2b && cfg.channel !== 'ws_willcall') return r;
      return stampLines(v).then(function () { return r; });
    }).catch(function () { writing = false; });
  }

  /* Cart attributes are invisible at checkout — Shopify renders line item
     PROPERTIES in the order summary, not attributes. Before this, a wholesale
     buyer confirmed an order without ever seeing the Will Call date and time
     they had chosen (audit finding 20). A checkout UI extension would be the
     obvious tool, but extensions on the information/shipping/payment steps are
     Plus-only and this store is not going to Plus — and the build store is a
     Development store, so an extension would appear to work here and fail on
     her real plan. Line properties render on every plan, and they carry through
     to the order and its emails. */
  var LABEL = { ws_willcall: 'Will Call', pickup: 'Pickup' };

  /* Every stamp run carries a sequence number. A newer run supersedes an older
     one, and the older one stops at its next step instead of finishing its
     chain — see applyStamp for why that matters (A9). */
  var stampSeq = 0;

  /* The label cannot come from cfg alone: cfg is parsed from the scheduler
     section, and templates/cart.json is the ONLY template that renders it. Off
     the cart page cfg is null, and the old expression then fell through to
     'Pickup' — which for a wholesale buyer is the wrong key entirely. The path
     is on <html> on every page (theme.liquid), so read it there. */
  function stampLabel() {
    if (cfg) return LABEL[cfg.channel] || (cfg.b2b ? 'Will Call' : 'Pickup');
    return (document.documentElement.getAttribute('data-mariposa-path') || '') === 'wholesale'
      ? 'Will Call' : 'Pickup';
  }

  /* Re-stamp when the cart changes. A line added after the date and time were
     already chosen carried no note at all (browser retest, 2026-08-22): the
     stamp only ran from writeAttributes, and the change guard then matched. */
  function restampFromCart() {
    /* Wholesale buyer chose Delivery: strip any Will Call stamp a prior
       selection left on the lines, so a shipped order never carries a "Will
       Call: <date>" label into checkout, the order or its emails (browser test
       2026-09-16, both lines kept the label after switching to Delivery). Reuses
       the normal clear path — applyStamp with an empty value, whose stampProps
       drops the Will Call AND Pickup families — and runs only for ws_fulfillment
       'ship', so willcall/pickup/retail carts are untouched. Reads the cart once
       to see the choice; on 'ship' it clears and returns before any stamping. */
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (c && c.attributes && c.attributes.ws_fulfillment === 'ship') {
          return clearWillCallStamps(c);
        }
        return restampCollect();
      })
      .catch(function () { return restampCollect(); });
  }

  /* Strip the Will Call / Pickup line-item stamp from every line, deterministically.
     Standalone (does NOT use the stampSeq/applyStamp chain, whose sequence guard
     was aborting this clear when init's other stamp runs bumped the sequence).
     Used when a wholesale buyer chooses Delivery: a shipped order must not carry a
     "Will Call: <date>" label into checkout, the order or its emails. Pass a cart
     snapshot only as a hint (the pass re-reads the cart). Sequential writes, quantity preserved
     (change.js re-keys the line and resets omitted quantity to 1).
     Resolves true when the cart ends with no stamped line AND every variant's total
     quantity equals what it was before the strip began; false otherwise.

     ADD FIRST, THEN REMOVE THE OLD LINE BY KEY (2026-10-10, QA-wholesale issue 1).
     This used to remove each line and then re-add it. A Checkout click (or any
     navigation) between the two calls left the line OUT of the cart, and the reloaded
     page then ran a second strip on the same lines: one run lost Almond Biscotti x3,
     another turned 5 cases into 7. Adding first means an interrupted chain can only
     ever leave an EXTRA line behind, never a missing one, and the extra is resolved
     against the pre-strip per-variant totals, which are kept in sessionStorage (keyed
     to the cart token) until a pass verifies them, so a pass started by the next page
     load removes the stamped leftover instead of adding it a second time. The Mail
     Order stamp code above (mailLinePass) has always added first for the same reason. */
  var WC_STRIP_KEY = 'ms-wc-strip';
  function wcStamped(it) {
    var p = it.properties || {};
    // "first non-empty" is also how the carrier callback reads the stamp (web/app/lib/will-call-rate.js)
    return !!(p['Will Call'] || p['Pickup']);
  }
  function wcTotals(c) {
    var t = {};
    ((c && c.items) || []).forEach(function (i) { t[i.id] = (t[i.id] || 0) + i.quantity; });
    return t;
  }
  function wcSaved(token) {
    try {
      var o = JSON.parse(sessionStorage.getItem(WC_STRIP_KEY) || 'null');
      if (o && o.token === token && Date.now() - o.at < 30 * 60 * 1000) return o.targets;
      // Expired or from another cart: drop it, or every later page load waits for a resume.
      if (o) sessionStorage.removeItem(WC_STRIP_KEY);
    } catch (e) {}
    return null;
  }
  function wcResumePending() {
    try {
      var o = JSON.parse(sessionStorage.getItem(WC_STRIP_KEY) || 'null');
      if (o && Date.now() - o.at < 30 * 60 * 1000) return true;
      if (o) sessionStorage.removeItem(WC_STRIP_KEY);
    } catch (e) {}
    return false;
  }
  function wcSave(token, targets) {
    try {
      if (targets) sessionStorage.setItem(WC_STRIP_KEY, JSON.stringify({ token: token, targets: targets, at: Date.now() }));
      else sessionStorage.removeItem(WC_STRIP_KEY);
    } catch (e) {}
  }
  /* Single-flight: the cart-page load runs this from init AND from restampFromCart, and
     two overlapping strips over the same lines is what doubled cases in QA. */
  var wcStripP = null;
  function clearWillCallStamps(snapshot) {
    if (wcStripP) return wcStripP;
    /* The snapshot only told the caller a strip is needed. The pass always reads a
       FRESH cart: a snapshot taken before another strip finished still lists lines that
       strip already rebuilt, and adding from it would double them. */
    /* Saved totals mean a strip on an earlier page was interrupted (reload, back, link).
       Its last write may still be landing: an add sent just before the reload was applied
       AFTER the next page read the cart, and that page added the same case again (390px,
       reload at 900 ms, coffeecake 1 -> 2). Give in-flight writes time to land first. */
    var resume = wcResumePending();
    var start = resume ? new Promise(function (res) { setTimeout(res, 2000); }) : Promise.resolve();
    wcStripP = start.then(function () { return wcStripPass(null, 0, null); })
      .then(function (ok) { wcStripP = null; return ok; }, function (e) { wcStripP = null; throw e; });
    return wcStripP;
  }
  function wcStripPass(_unused, attempt, targets) {
    attempt = attempt || 0;
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    }).then(function (c) {
      var items = (c && c.items) || [];
      var dirty = items.filter(wcStamped);
      if (!targets) {
        targets = wcSaved(c.token);
        if (!targets) {
          if (!dirty.length) return true;
          targets = wcTotals(c);
          wcSave(c.token, targets);
        }
      }
      var cur = wcTotals(c);
      if (!dirty.length) {
        /* Backstop for the same race: a variant ABOVE its pre-strip total can only be an
           add that landed twice (the buyer's quantities are locked while a strip runs), so
           trim it back to the saved total before calling the cart clean. */
        var over = Object.keys(targets).filter(function (v) { return (cur[v] || 0) > targets[v]; });
        if (over.length && attempt < 3) {
          var trim = Promise.resolve();
          over.forEach(function (v) {
            var excess = cur[v] - targets[v];
            items.filter(function (i) { return String(i.id) === v; }).reverse().forEach(function (line) {
              trim = trim.then(function () {
                if (excess <= 0) return;
                var cut = Math.min(excess, line.quantity);
                return fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: line.key, quantity: line.quantity - cut })
                }).then(function (r) { if (r.ok) excess -= cut; }).catch(function () {});
              });
            });
          });
          return trim.then(function () { return wcStripPass(null, attempt + 1, targets); });
        }
        var same = Object.keys(targets).every(function (v) { return (cur[v] || 0) === targets[v]; }) &&
          Object.keys(cur).every(function (v) { return v in targets; });
        // Nothing stamped is left, so nothing can be added twice: drop the saved totals.
        wcSave(c.token, null);
        var done = function () { return same; };
        return attempt > 0 ? refreshCartSection().then(done, done) : same;
      }
      if (attempt >= 3) { var no = function () { return false; }; return refreshCartSection().then(no, no); }
      var chain = Promise.resolve();
      var refused = 0;
      /* /cart/change.js with a properties object that OMITS a key does NOT delete the
         key (proven on this store 2026-09-16), and an empty properties object is
         ignored (2026-09-21). So: add a clean line for the variant, then remove the
         stamped line by its key. Wholesale price comes from the company price list,
         not a line property, so it survives. A clean line that already exists for the
         variant absorbs the add; quantities are what matters and are verified below. */
      dirty.forEach(function (it) {
        var clean = {};
        Object.keys(it.properties || {}).forEach(function (k) {
          if (k !== 'Will Call' && k !== 'Pickup') clean[k] = it.properties[k];
        });
        chain = chain.then(function () {
          var target = (it.id in targets) ? targets[it.id] : cur[it.id];
          var others = (cur[it.id] || 0) - it.quantity;
          var need = target - others;
          var removeOld = function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: it.key, quantity: 0 })
            }).then(function (r2) {
              if (!r2.ok) { refused += 1; return; }
              cur[it.id] = others + Math.max(need, 0);
            });
          };
          // An earlier, interrupted pass already added this line back: just drop the stamped copy.
          if (need <= 0) return removeOld().catch(function () { refused += 1; });
          var body = { id: it.id, quantity: need };
          if (Object.keys(clean).length) body.properties = clean;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }).then(function (r) {
            if (r.ok) return removeOld();
            /* Add refused (e.g. an inventory cap while both lines coexist): blank the
               stamp in place instead, one atomic write that keeps the quantity. An
               empty-valued property renders nowhere and the callback ignores it. */
            var blank = {};
            Object.keys(it.properties || {}).forEach(function (k) { blank[k] = it.properties[k]; });
            if ('Will Call' in blank) blank['Will Call'] = '';
            if ('Pickup' in blank) blank['Pickup'] = '';
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: blank })
            }).then(function (r3) { if (!r3.ok) refused += 1; });
          }).catch(function () { refused += 1; });
        });
      });
      /* Re-read and verify; retry (fresh keys) if any write was refused (429/422) or
         an interrupted line is still there. The next pass finds nothing to do when
         every write took, and checks the totals. */
      return chain.then(function () {
        var wait = refused ? 500 * (attempt + 1) : 0;
        return new Promise(function (res) { setTimeout(res, wait); })
          .then(function () { return wcStripPass(null, attempt + 1, targets); });
      });
    }).catch(function () { return false; });
  }

  /* While a wholesale fulfilment switch (or the strip / restamp it causes) is in flight
     the cart is mid-rewrite, so Checkout, quantity changes and the radios wait for it
     (QA-wholesale issue 1: a Checkout click during the old strip dropped or duplicated
     cases). Blocks clicks/changes/Enter in capture phase so re-rendered nodes are
     covered too; the busy state ends at the page reload or, on failure, here. */
  var wsBusyN = 0;
  function wsBusy(on) {
    wsBusyN = Math.max(0, wsBusyN + (on ? 1 : -1));
    var busy = wsBusyN > 0;
    document.documentElement.classList.toggle('ms-ws-busy', busy);
    if (!document.getElementById('ms-ws-busy-css')) {
      var st = document.createElement('style');
      st.id = 'ms-ws-busy-css';
      st.textContent = 'html.ms-ws-busy cart-items,html.ms-ws-busy [data-ms-ws-choice],html.ms-ws-busy #main-cart-footer .cart__ctas{pointer-events:none;opacity:.55}' +
        'html.ms-ws-busy #main-cart-footer .cart__ctas{cursor:progress}';
      document.head.appendChild(st);
    }
    document.querySelectorAll('#main-cart-footer button[name="checkout"], cart-drawer button[name="checkout"]').forEach(function (b) {
      if (busy) {
        if (!b.hasAttribute('data-ms-busy-label')) {
          b.setAttribute('data-ms-busy-label', b.innerHTML);
          b.setAttribute('data-ms-busy-was-disabled', b.disabled ? '1' : '');
        }
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
        b.textContent = 'Updating…';
      } else if (b.hasAttribute('data-ms-busy-label')) {
        b.innerHTML = b.getAttribute('data-ms-busy-label');
        b.disabled = b.getAttribute('data-ms-busy-was-disabled') === '1' || b.hasAttribute('data-ms-ws-wait');
        b.removeAttribute('data-ms-busy-label');
        b.removeAttribute('data-ms-busy-was-disabled');
        b.removeAttribute('aria-busy');
      }
    });
    document.querySelectorAll('cart-items input.quantity__input, [data-ms-ws-choice]').forEach(function (i) { i.disabled = busy; });
    if (!busy) { syncMinShort(); syncCheckoutTotal(); applyB2bConflictState(); applyBakeshopConflictState(); }
  }
  function wsBusyGuard(e) {
    if (!wsBusyN || !e.target || !e.target.closest) return;
    var t = e.target;
    var hit = t.closest('button[name="checkout"], cart-items, [data-ms-ws-choice], #main-cart-footer .cart__ctas, form#cart');
    if (!hit) return;
    if (e.type === 'keydown' && e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }
  ['click', 'change', 'input', 'keydown', 'submit'].forEach(function (type) {
    document.addEventListener(type, wsBusyGuard, true);
  });

  /* Wholesale cart page: main-cart-footer.liquid renders Checkout DISABLED with
     data-ms-ws-wait ("Checking your cart…") for a B2B buyer, because this file is
     deferred and a tap before it ran was a plain submit straight to checkout, with
     no Will Call date or stamp check (R-fixbatch R3: 3 of 4 phone runs). On Grow the
     theme is the only gate, so the button stays closed until init's page-load cart
     check (and any strip or restamp it starts) has settled. The CSS for the two
     labels keys off the attribute, so wsBusy's label save/restore never fights it. */
  var WS_WAIT_MS = 10000;
  var wsWaitDone = false;
  var wsGateBound = false; // init() reached the Checkout click gate below
  function wsWaitBtn() {
    return document.querySelector('#main-cart-footer button[name="checkout"][data-ms-ws-wait]');
  }
  function wsWaitRelease(slow) {
    if (wsWaitDone) return;
    wsWaitDone = true;
    var b = wsWaitBtn();
    if (!b) return;
    if (!wsGateBound) {
      /* init() threw before the Checkout click gate was attached: opening the button now
         would be a plain submit with no Will Call date check (R-final round 3, case 2).
         Keep it closed and give the number, as when the script never loads. */
      var h0 = document.querySelector('#main-cart-footer [data-ms-ws-wait-help]');
      if (h0) h0.hidden = false;
      return;
    }
    b.removeAttribute('data-ms-ws-wait');
    if (slow) {
      // The check never answered: open Checkout (the click gate still re-reads the
      // cart) and say who to call, the same number the site gives everywhere else.
      var help = document.querySelector('#main-cart-footer [data-ms-ws-wait-help]');
      if (help) help.hidden = false;
    }
    // While busy, wsBusy(false) re-applies the disabled state when the busy ends.
    if (!wsBusyN) { syncMinShort(); applyB2bConflictState(); }
  }
  /* The init strip resolved false: say so, as the radio path does. */
  function showWsStripError() {
    var text = 'We could not update every item in your cart. Please check your quantities before checking out.';
    var anchor = el('[data-ms-ws-choice]');
    var host = anchor && anchor.parentNode && anchor.parentNode.parentNode;
    if (!host) { showCheckoutError(text); return; }
    var notice = document.querySelector('[data-ms-ws-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute('data-ms-ws-error', '');
      notice.setAttribute('role', 'alert');
      host.appendChild(notice);
    }
    notice.textContent = text;
  }

  function restampCollect() {
    if (cfg) {
      /* On the cart page the PANEL is authoritative, not the attributes: a
         pickup_date the rules no longer offer is dropped by populateDates, and
         stamping the lines from the attribute anyway would put an unavailable
         date on them. Behaviour here is unchanged. */
      var v = currentValues();
      return (v.date && v.time) ? stampLines(v) : Promise.resolve();
    }
    /* A9, and the half of it that needs no race to reproduce. currentValues()
       reads the three <select>s in the scheduler panel, and that panel renders
       on /cart and nowhere else — so on a PDP, a collection, Quick Order or the
       drawer, cfg is null, currentValues() is empty, and this returned without
       stamping anything. A line added from any of those pages reached the order
       carrying NO note while the cart-level date read correctly: exactly order
       #1031's "California Lemon Cake 4\" - 8 cake/cs", the one line of five with
       no Will Call property. Fall back to the cart's own attributes, which hold
       the same three values the panel would have shown. */
    return stampFromAttributes();
  }

  /* Stamp from cart attributes rather than from the DOM. Deliberately narrow:
     it runs only on the two paths that collect (a mail cart must never grow a
     Pickup note), only when all three attributes are present, and never when a
     wholesale buyer has chosen Delivery over Will Call. */
  function stampFromAttributes() {
    var path = document.documentElement.getAttribute('data-mariposa-path') || '';
    if (path !== 'pickup' && path !== 'wholesale') return Promise.resolve();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var a = (c && c.attributes) || {};
        if (!c || !c.items || !c.items.length) return;
        if (a.ws_fulfillment === 'ship') return;
        if (!a.pickup_date || !a.pickup_time || !a.pickup_location) return;
        return stampLines({ location: a.pickup_location, date: a.pickup_date, time: a.pickup_time });
      })
      .catch(function () {});
  }

  function stampProps(item, label, value) {
    var props = {};
    Object.keys(item.properties || {}).forEach(function (k) {
      // Drop BOTH families: the other pickup label, and the Mail Order
      // ship/arrival stamp, so a cart that changed path never carries two
      // contradictory promises into checkout.
      if (k !== 'Will Call' && k !== 'Pickup' && k !== MAIL_SHIP_KEY && k !== MAIL_ARRIVE_KEY) {
        props[k] = item.properties[k];
      }
    });
    if (value) props[label] = value;
    return props;
  }

  /* The line stamp's value for a schedule. One definition, shared by stampLines() and
     the two gates that verify a cart before letting it go (express checkout, Will Call). */
  function stampValue(v) {
    var label = stampLabel();
    var value = '';
    if (v && v.date && v.time) {
      value = readableDate(v.date) + ', ' + readableTime(v.time);
      // the label already says "Will Call"; repeating it reads as a mistake
      if (v.location && v.location !== label) value += ' — ' + v.location;
    }
    return value;
  }

  function stampLines(v) {
    var label = stampLabel();
    var value = stampValue(v);
    stampSeq += 1;
    return applyStamp(label, value, stampSeq, 0, false);
  }

  /* THE A9 FIX. What this replaced: one pass over a /cart.js snapshot, firing
     N fire-and-forget /cart/change.js calls whose RESPONSE STATUS WAS NEVER
     READ. A 4xx or 5xx does not reject a fetch promise, so `.catch(function(){})`
     never saw one — every refused write was silently discarded and the line kept
     the date it already had, while the cart-level attributes (written first, by
     writeAttributes) showed the new one. That is the shape of Eileen's #1031:
     cart-level 2026-09-10, four lines still reading "Wed 9 Sep 2026, 10:00 am".
     Two things make those writes fail in practice, both measured on this store:
       - HTTP 429. The storefront rate-limits /cart/*.js and answers 429 (twice
         within this session's own testing). Nothing retried.
       - HTTP 422. /cart/change.js addresses a line by KEY, and a key is
         variant + properties — so the moment one chain stamps a line, that
         line's key changes and any other chain still holding the old key gets
         "line parameter is invalid." Two chains overlapped routinely: writeAttributes()
         did not await its own stampLines call, so the checkout gate's
         writeAttributes().then(restampFromCart) started a SECOND chain while the
         first was mid-flight, and every cartUpdate started another. When the two
         chains carried DIFFERENT values (date changed while a chain was still
         writing) the loser's rows kept the older date. Sequence numbers now stop
         a superseded chain at its next step, so only one value is ever in flight.
     So: read the status, and verify. After the writes, re-read the cart and
     check that every line actually carries the value. Anything still wrong is
     re-tried against FRESH keys, up to three times with a short backoff. The
     verification pass is not optional — it is the only thing that turns a silent
     refusal into a corrected line. */
  function applyStamp(label, value, seq, attempt, didWrite) {
    if (seq !== stampSeq) return Promise.resolve();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (seq !== stampSeq) return;
        var todo = (cart.items || []).filter(function (item) {
          return (((item.properties || {})[label]) || '') !== value;
        });
        if (!todo.length) return didWrite ? refreshCartSection() : undefined;
        if (attempt >= 3) return didWrite ? refreshCartSection() : undefined;
        var chain = Promise.resolve();
        var refused = 0;
        todo.forEach(function (item) {
          chain = chain.then(function () {
            if (seq !== stampSeq) return;
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // MUST send quantity: /cart/change.js re-keys the line by
              // variant+properties, and omitting quantity resets it to 1 — which
              // silently dropped min-qty lines below their minimum right after the
              // shopper picked a date (browser QA 2026-09-03, reproduced twice).
              //
              // ADDRESSED BY LINE KEY, NOT BY INDEX (2026-09-06). The suspicion was
              // that a properties change re-keys the line and therefore MOVES it, so
              // `line: i + 1` over a pre-change snapshot would stamp the wrong rows
              // from the second line onward. THAT WAS TESTED AND IS FALSE: a
              // three-line cart kept its exact order across a properties change
              // (/cart.js before and after). The key form is kept because it names
              // the row instead of its position. The key going STALE is a different
              // matter and is exactly what the retry above exists for.
              body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: stampProps(item, label, value) })
            }).then(function (r) { if (!r.ok) refused += 1; })
              .catch(function () { refused += 1; });
          });
        });
        return chain.then(function () {
          if (seq !== stampSeq) return;
          // Always come back round: the write may have been refused (status
          // read above) or landed on a row another chain had already moved.
          // The next pass reads fresh keys and fixes whatever is still wrong,
          // and finds nothing to do when the writes all took.
          var wait = refused ? 500 * (attempt + 1) : 0;
          return new Promise(function (res) { setTimeout(res, wait); })
            .then(function () { return applyStamp(label, value, seq, attempt + 1, true); });
        });
      })
      .catch(function () {});
  }

  /* /cart/change.js re-keys the line, so Dawn's rendered cart is stale and the
     line sits on "Loading..." until the shopper reloads — found by a browser test
     on 2026-08-22, on the very note that tells them when they are collecting.
     Re-render the cart section the way Dawn does. Shared by the Will Call stamp
     and the Mail Order ship/arrival stamp. */
  function refreshCartSection() {
        /* The section ID is NOT the section TYPE. templates/cart.json names this
           section "cart-items"; "main-cart-items" is the file it is built from.
           Asking for the type returned nothing to swap, which is why the note
           kept only appearing after a reload. Read the id off the DOM so this
           cannot drift again: Dawn wraps it in <div id="shopify-section-...">. */
        var host = document.querySelector('cart-items') || document.querySelector('.js-contents');
        var wrap = host && host.closest('[id^="shopify-section-"]');
        var sectionId = wrap ? wrap.id.replace('shopify-section-', '') : 'cart-items';
        /* The DRAWER too (2026-10-10, W-drawer-date.md). A PDP add into a cart that is
           already scheduled lands as its own unstamped line (properties differ), Dawn paints
           the drawer from that cart ("minimum 4, you have 1", Checkout disabled), and the
           stamp below then merges it into the stamped line. Nothing re-rendered the drawer,
           so it kept the pre-merge state until a reload. Same swap Dawn's CartDrawerItems
           does (.drawer__inner), which leaves the overlay and its close listener alone. */
        var drawerInner = document.querySelector('cart-drawer .drawer__inner');
        var sections = drawerInner ? [sectionId, 'cart-drawer'] : [sectionId];
        // A2 (2026-09-04): the GET /cart?section_id fetch is cacheable, and after a
        // date change it returned an EARLIER cart snapshot — so one line showed its
        // pre-change date and a line added later showed none, until a full reload.
        // cache:'no-store' + a cache-buster did not defeat it. Use the Section
        // Rendering API via POST /cart/update.js instead: a POST is uncacheable and
        // its `sections` payload is rendered from the current (post-change) cart, so
        // every line reflects the new date without a reload.
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sections: sections, sections_url: window.location.pathname })
        })
          .then(function (r) { return r.json(); })
          .then(function (state) {
            var dHtml = drawerInner && state && state.sections && state.sections['cart-drawer'];
            var dFresh = typeof dHtml === 'string' ? new DOMParser().parseFromString(dHtml, 'text/html').querySelector('.drawer__inner') : null;
            var dLive = document.querySelector('cart-drawer .drawer__inner');
            if (dFresh && dLive) dLive.innerHTML = dFresh.innerHTML;
            var html = state && state.sections && state.sections[sectionId];
            if (!html) return;
            var fresh = new DOMParser().parseFromString(html, 'text/html');
            // A2 (2026-09-04): there are multiple .js-contents nodes in the live cart
            // DOM but exactly one <cart-items>, so swapping the FIRST .js-contents
            // updated the wrong node and the visible lines kept their stale date
            // (Plain showed the pre-change date, a later-added line showed none).
            // Swap the single <cart-items> element's contents instead — proven in
            // browser to re-stamp every line with the new date, no reload.
            var freshCI = fresh.querySelector('cart-items');
            var liveCI = document.querySelector('cart-items');
            if (freshCI && liveCI) {
              liveCI.innerHTML = freshCI.innerHTML;
            } else {
              var src = fresh.querySelector('.js-contents');
              var dst = document.querySelector('.js-contents');
              if (src && dst) dst.innerHTML = src.innerHTML;
            }
          })
          .catch(function () {});
  }

  /* ---- EXPRESS CHECKOUT GATE (2026-10-10) ------------------------------------
     Shop Pay / Apple Pay / Google Pay on /cart go straight to checkout and never pass the
     Checkout-button gate below, and on Grow no checkout Function refuses a bad cart. So on
     a cart holding a pickup line the buttons are rendered hidden (sections/
     main-cart-footer.liquid) and shown only when the CART ITSELF, re-read from /cart.js,
     carries the date, time and location the picker shows (all five attributes, and the
     matching stamp on every line), the slot is still valid, no conflict applies and no
     line is under its minimum. Any change hides them at once; confirming shows them again.
     The state is the ms-xp-gated class on [data-ms-express]. Design: scratchpad findings
     N-express-pickup.md. Mail Order carts start shown and are hidden only while
     something unsaved (gift note, order note) or a blocking state applies. */
  var xpSeq = 0;
  var XP_RETRIES = 5;
  function xpWrap() { return document.querySelector('#main-cart-footer [data-ms-express]'); }
  function xpNeedsSchedule(w) { return !!(w && w.hasAttribute('data-ms-express-needs-schedule')); }
  function xpHide() {
    xpSeq += 1;                       // invalidates any confirm in flight
    var w = xpWrap();
    if (w) w.classList.add('ms-xp-gated');
  }
  /* Pure: the picker's values are complete and still offered. Unlike pickupComplete()
     it never rebuilds the time <select>. */
  function scheduleStillValid() {
    if (!cfg || !cfg.hasPickupLines) return false;
    var v = currentValues();
    if (!(v.location && v.date && v.time)) return false;
    if (!cfg.locations || !cfg.locations[v.location]) return false;
    if (!validDates(v.location).some(function (d) { return iso(d) === v.date; })) return false;
    return timeSlotsFor(v.location, v.date).indexOf(v.time) !== -1;
  }
  function xpNorm(x) { return String(x == null ? '' : x).replace(/\r\n/g, '\n').trim(); }
  /* A gift note or order note still inside its save debounce would be lost to a wallet
     tap, so the cart must already hold what the boxes show. */
  function xpNotesSaved(c) {
    var g = document.querySelector('[data-ms-gift-note]');
    if (g && xpNorm((c.attributes || {}).gift_note) !== xpNorm(g.value)) return false;
    var n = document.getElementById('Cart-note');
    if (n && xpNorm(c.note) !== xpNorm(n.value)) return false;
    return true;
  }
  /* The minimum-quantity state is Liquid-only (a product metafield). Render the footer
     through the Section Rendering API — a POST, which is uncacheable, the reason
     refreshCartSection() uses it — and read its [data-mb-short]. */
  function xpServerFlags() {
    var foot = document.getElementById('main-cart-footer');
    var sid = foot && foot.dataset.id;
    if (!sid) return Promise.resolve(false);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: [sid], sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (st) {
        var h = st && st.sections && st.sections[sid];
        if (typeof h !== 'string') return false;
        var short = new DOMParser().parseFromString(h, 'text/html').querySelector('[data-mb-short]');
        return !!short && short.textContent.trim() === '';
      })
      .catch(function () { return false; });
  }
  function xpConfirm(retry) {
    retry = retry || 0;
    var w = xpWrap();
    if (!w) return Promise.resolve();
    var needs = xpNeedsSchedule(w);
    var seq = ++xpSeq;
    var hide = function () { if (seq === xpSeq) w.classList.add('ms-xp-gated'); };
    if (!cfg || cfg.b2b || cfg.bakeshopConflict || cfg.b2bMailConflict) { hide(); return Promise.resolve(); }
    if (needs && (writing || !scheduleStillValid())) { hide(); return Promise.resolve(); }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (c) {
        if (seq !== xpSeq) return null;
        var a = c.attributes || {};
        if (a.gift_plan || !c.item_count) return 'off';
        if (!xpNotesSaved(c)) return 'pending';
        if (needs) {
          var v = currentValues(), label = stampLabel(), sv = stampValue(v);
          var match = a.pickup_date === v.date && a.pickup_time === v.time &&
            a.pickup_location === v.location && a['pick-up-location'] === v.location &&
            a.delivery_date === legacyDeliveryDate(v.date, v.time) &&
            (c.items || []).every(function (i) { return ((i.properties || {})[label] || '') === sv; });
          if (!match) return 'pending';
        }
        return xpServerFlags().then(function (ok) { return ok ? 'ok' : 'off'; });
      })
      .then(function (state) {
        if (state === null || seq !== xpSeq) return;
        if (state === 'ok') { w.classList.remove('ms-xp-gated'); return; }
        w.classList.add('ms-xp-gated');
        /* 'pending' = the cart has not caught up yet (a stamp chain or a note save still
           in flight, or a refused write being retried). Look again shortly. */
        if (state === 'pending' && retry < XP_RETRIES) {
          setTimeout(function () { if (seq === xpSeq) xpConfirm(retry + 1); }, 1500);
        }
      })
      .catch(hide);
  }
  /* Time passing a cutoff (a phone tab left open is the usual case). The pure check only:
     it must not rebuild the time <select> under an open dropdown. */
  function xpTick() {
    var w = xpWrap();
    if (!w || document.hidden) return;
    if (xpNeedsSchedule(w) && !scheduleStillValid()) { xpHide(); return; }
    if (w.classList.contains('ms-xp-gated')) xpConfirm();
  }

  /* ---- CART DRAWER PICKUP CHECK (2026-10-10) --------------------------------------
     The drawer has no date picker. It used to send a cart to checkout whenever the three
     pickup attributes were present and the date was not before today, so a date that had
     stopped being valid — the 2pm cutoff passed, or an item with a date window or a lead
     time added after scheduling — went straight through (scratchpad G-drawer-paths.md §2:
     both reproduced). On Grow no checkout Function catches it. The drawer now runs the
     cart page's own rules, validDates() plus the slot check, against the same config
     (snippets/mariposa-schedule-config.liquid, rendered in the drawer under its own
     attribute). Invalid: the button reads "Choose Pickup Time" with a one-line reason and
     goes to /cart, where the calendar already drops the bad date. Anything that cannot be
     checked fails closed to /cart. */
  function readDrawerCfg(root) {
    var n = (root || document).querySelector('[data-mariposa-drawer-schedule-config]');
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }
  /* true / false; null when there is nothing to check against. Swaps the drawer config
     in as cfg for one synchronous pass, the way fillShipEstimate() does. */
  function drawerPickupValid(dcfg, attrs) {
    if (!dcfg || !dcfg.locations) return null;
    var prev = cfg;
    cfg = dcfg;
    try {
      var loc = attrs && attrs.pickup_location, date = attrs && attrs.pickup_date, time = attrs && attrs.pickup_time;
      if (!(loc && date && time)) return false;
      if (!cfg.locations[loc]) return false;
      if (!validDates(loc).some(function (d) { return iso(d) === date; })) return false;
      return timeSlotsFor(loc, date).indexOf(time) !== -1;
    } catch (e) {
      return null;
    } finally {
      cfg = prev;
    }
  }
  var DRAWER_INVALID_HTML = 'Your pickup date isn\u2019t available for everything in your cart \u2014 <a href="/cart">choose a new one</a>';
  /* Show the result before the click. Writes only when something differs, so the drawer's
     MutationObserver settles. */
  function drawerPaint() {
    var drawer = document.querySelector('cart-drawer');
    var summary = drawer && drawer.querySelector('[data-mariposa-drawer-summary]');
    if (!summary || summary.dataset.complete !== 'true') return;
    var dcfg = readDrawerCfg(drawer);
    if (!dcfg) return;
    var ok = drawerPickupValid(dcfg, {
      pickup_location: summary.dataset.pickupLocation,
      pickup_date: summary.dataset.pickupDate,
      pickup_time: summary.dataset.pickupTime
    });
    if (ok !== false) return;
    var btn = document.getElementById('CartDrawer-Checkout');
    if (btn && btn.textContent.trim() !== 'Choose Pickup Time') btn.textContent = 'Choose Pickup Time';
    if (summary.getAttribute('data-ms-drawer-state') !== 'invalid') {
      summary.setAttribute('data-ms-drawer-state', 'invalid');
      summary.innerHTML = '<span class="ms-missing">' + ICON_PICKUP + ' ' + DRAWER_INVALID_HTML + '</span>';
    }
  }
  /* At the click: re-render the drawer from the CURRENT cart (POST, uncacheable) and check
     that, not the DOM, which may be from before another tab or a long pause changed things.
     A failed fetch is a failed check. */
  function freshDrawerCheck() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: ['cart-drawer'], sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (st) {
        var html = st && st.sections && st.sections['cart-drawer'];
        if (typeof html !== 'string') return false;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sum = doc.querySelector('[data-mariposa-drawer-summary]');
        if (!sum) return !!doc.querySelector('#CartDrawer');   // no pickup line any more
        if (sum.getAttribute('data-complete') !== 'true') return false;
        var dcfg = readDrawerCfg(doc);
        return drawerPickupValid(dcfg, (dcfg && dcfg.attributes) || {}) === true;
      })
      .catch(function () { return false; });
  }
  /* Mail Order drawer checkout: write the three ship attributes if the cart does not
     already carry them. Never blocks the checkout (the order webhook recomputes ship_date
     for staff); it only makes the email and slips match what the drawer showed. */
  function ensureShipAttributes(vals) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var a = (c && c.attributes) || {};
        var fresh = Object.keys(vals).every(function (k) { return (a[k] || '') === vals[k]; });
        return fresh ? true : writeCartAttributes(vals);
      })
      .catch(function () { return false; });
  }

  /* ---- WHOLESALE CART RULES (2026-10-10) ------------------------------------------
     The checkout-rules Function refused, for a B2B cart: 15 or more non-gift-card units,
     and a Will Call-only line (requires_shipping=false, the sheet pan) on a Delivery
     order. It does not run on Grow, so the cart page blocks both, with the Function's own
     wording, the same way it blocks a retail line in a wholesale cart. Both come from
     /cart.js, so they stay live as quantities change. */
  function b2bBlocked() {
    return !!(cfg && cfg.b2b && (cfg.b2bMailConflict || cfg.b2bCaseLimit || cfg.b2bCollectShip));
  }
  function recheckB2bCart() {
    if (!cfg || !cfg.b2b) return Promise.resolve(null);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (c) {
        var units = 0, collectOnly = false;
        (c.items || []).forEach(function (i) {
          if (i.gift_card) return;
          units += i.quantity;
          if (!i.requires_shipping) collectOnly = true;
        });
        cfg.b2bCaseLimit = units >= 15;
        cfg.b2bCollectShip = collectOnly && (c.attributes || {}).ws_fulfillment === 'ship';
        applyB2bConflictState();
        applyBakeshopConflictState();
        return c;
      });
  }
  function scrollToB2bBanner() {
    var sel = ['[data-ms-b2b-conflict]', '[data-ms-b2b-cases]', '[data-ms-b2b-collect-ship]'];
    for (var i = 0; i < sel.length; i++) {
      var b = el(sel[i]);
      if (b && !b.hidden) { b.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    }
  }
  /* Will Call: the carrier callback decides Will Call vs FedEx from the "Will Call" line
     stamp (H-wholesale-verify.md change 1), so before the checkout continues the cart is
     re-read and must carry the stamp on EVERY line, plus the schedule and
     ws_fulfillment=willcall. One retry (verified attribute write + a fresh stamp chain);
     still wrong means stay on the cart and say so. */
  function willCallReady() {
    var v = currentValues();
    var want = {
      pickup_date: v.date,
      pickup_time: v.time,
      pickup_location: v.location,
      delivery_date: legacyDeliveryDate(v.date, v.time),
      'pick-up-location': v.location,
      ws_fulfillment: 'willcall'
    };
    var label = stampLabel(), sv = stampValue(v);
    function check() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (c) {
          var a = c.attributes || {};
          var items = c.items || [];
          return {
            attrs: Object.keys(want).every(function (k) { return (a[k] || '') === want[k]; }),
            lines: !!sv && items.length > 0 && items.every(function (i) { return ((i.properties || {})[label] || '') === sv; })
          };
        });
    }
    return check().then(function (st) {
      if (st.attrs && st.lines) return true;
      return (st.attrs ? Promise.resolve(true) : writeCartAttributes(want))
        .then(function () { return st.lines ? null : stampLines(v); })
        .then(check)
        .then(function (st2) { return st2.attrs && st2.lines; });
    }).catch(function () { return false; });
  }
  function showCheckoutError(text) {
    var anchor = el('[data-ms-block="pickup"]') || el('#main-cart-footer .cart__ctas');
    if (!anchor) return;
    var n = document.querySelector('[data-ms-checkout-error]');
    if (!n) {
      n = document.createElement('p');
      n.className = 'ms-error';
      n.setAttribute('data-ms-checkout-error', '');
      n.setAttribute('role', 'alert');
      n.style.display = 'block';
      if (anchor.matches('.cart__ctas')) anchor.parentNode.insertBefore(n, anchor);
      else anchor.appendChild(n);
    }
    n.textContent = text;
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function clearCheckoutError() {
    var n = document.querySelector('[data-ms-checkout-error]');
    if (n) n.remove();
  }
  /* Delivery: the carrier callback treats ANY line still carrying a non-empty "Will
     Call" (or "Pickup") stamp as a Will Call cart and offers only the $0 Will Call
     rate (web/app/routes/carrier.wholesale-rates.jsx). So before a Delivery checkout
     continues: join any strip still running (or run one; it no-ops on a clean cart),
     re-read the cart, and require ws_fulfillment=ship with NO stamped line
     (R-fixbatch R2: a click mid-strip reached a Will Call-only checkout). Busy while
     the strip runs, so quantities wait too. */
  function deliveryReady(c) {
    var strip = !!wcStripP || ((c && c.items) || []).some(wcStamped);
    if (strip) wsBusy(true);
    return clearWillCallStamps().then(function (ok) {
      if (strip) wsBusy(false);
      if (ok === false) return false;
      return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (c2) {
          return ((c2.attributes || {}).ws_fulfillment === 'ship') && !(c2.items || []).some(wcStamped);
        });
    }, function () { if (strip) wsBusy(false); return false; }).catch(function () { return false; });
  }
  var b2bChecking = false;
  function b2bCheckout(btn) {
    // One check at a time: a double tap used to start two overlapping write chains.
    if (b2bChecking) return Promise.resolve();
    b2bChecking = true;
    /* Wholesale order review (2026-10-16): with shop metafield mariposa.ws_order_builder
       on, main-cart-footer.liquid renders this button as "Review wholesale order" with
       data-ms-ws-review. Every check below still runs; once the cart is ready the buyer
       goes to the review page instead of Shopify checkout. Without the attribute this
       is the original submit. */
    var review = btn.getAttribute('data-ms-ws-review');
    var again = review ? 'Review wholesale order' : 'Checkout';
    var go = function () {
      clearCheckoutError();
      if (review) { window.location.href = review; return; }
      btn.dataset.msArmed = '1'; btn.click();
    };
    return recheckB2bCart().then(function (c) {
      if (b2bBlocked()) { scrollToB2bBanner(); return; }
      var willcall = ((c && c.attributes) || {}).ws_fulfillment !== 'ship';
      /* The page was rendered for the other choice (changed in another tab): the blocks
         on screen are the wrong ones, so re-render rather than guess. */
      if (willcall !== (cfg.channel === 'ws_willcall')) { window.location.reload(); return; }
      if (!willcall) {
        return deliveryReady(c).then(function (ok) {
          if (ok) go();
          else showCheckoutError('We could not update every item in your cart. Please check your quantities and press ' + again + ' again.');
        });
      }
      if (!pickupComplete()) { markInvalid(); return; }
      return writeAttributes().then(restampFromCart).then(willCallReady).then(function (ok) {
        if (ok) go();
        else showCheckoutError('We could not save your Will Call date and time on every item in your cart. Please press ' + again + ' again.');
      });
    }).catch(function () {
      showCheckoutError('We could not check your cart just now. Please press ' + again + ' again.');
    }).then(function () { b2bChecking = false; });
  }

  /* Cart page minimum quantity: the warning and the disabled Checkout sit outside the
     .js-contents block Dawn re-renders, so after the shopper raised a short line they
     stayed until a reload (G-drawer-paths.md §2 side finding). The fresh state rides in
     [data-mb-short] inside .js-contents (main-cart-footer.liquid). */
  function syncMinShort() {
    var src = document.querySelector('#main-cart-footer [data-mb-short]');
    if (!src) return;
    var short = src.textContent.trim();
    var warn = document.querySelector('#main-cart-footer [data-mb-min-warning]');
    if (warn) {
      var list = warn.querySelector('[data-mb-min-list]');
      if (list && short) list.textContent = short;
      warn.hidden = !short;
    }
    var btn = document.querySelector('#main-cart-footer button[name="checkout"]');
    var foot = document.getElementById('main-cart-footer');
    if (btn) btn.disabled = !!short || !!(foot && foot.classList.contains('is-empty')) || btn.hasAttribute('data-ms-ws-wait');
  }

  function readableDate(iso) {
    var p = String(iso).split('-');
    if (p.length !== 3) return iso;
    var d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return days[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + mons[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
  }

  function readableTime(hhmm) {
    var b = String(hhmm).split(':');
    var h = +b[0];
    if (isNaN(h)) return hhmm;
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + ':' + (b[1] || '00') + ' ' + (h >= 12 ? 'pm' : 'am');
  }

  var ICON_PICKUP = '<svg class="mariposa-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 10v10h16V10"/><path d="M2 10l2-6h16l2 6"/><path d="M2 10h20"/><path d="M9 20v-6h6v6"/></svg>';

  function updateDrawerSummary() {
    var box = document.querySelector('[data-mariposa-drawer-summary]');
    if (!box) return;
    // Off the cart page there is no scheduler config: the server-rendered
    // summary (and its data-complete state) is authoritative — leave it alone.
    if (!cfg) return;
    if (!cfg.hasPickupLines) { box.innerHTML = ''; box.dataset.complete = 'true'; return; }
    var v = currentValues();
    if (v.location && v.date && v.time) {
      var p = v.date.split('-');
      var d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
      box.dataset.complete = 'true';
      box.innerHTML = ICON_PICKUP + ' Pickup: <strong>' + v.location + ', ' + dateLabel(d) + ', ' + timeLabel(v.time) +
        '</strong> · <a href="/cart">Edit</a>';
    } else {
      box.dataset.complete = 'false';
      box.innerHTML = '<span class="ms-missing">' + ICON_PICKUP + ' Pickup date needed — <a href="/cart">choose it in your cart</a></span>';
    }
  }

  /* Express "Buy it now" skips the cart where scheduling lives — hide it for
     pickup variants (dual products: checked Fulfillment radio; pickup-only
     products: liquid marker) so buyers can't dead-end at a blocked checkout. */
  function updateBuyNowVisibility() {
    var buttons = document.querySelectorAll('.shopify-payment-button');
    if (!buttons.length) return;
    var hide = false;
    var picked = document.querySelector('input[data-option-name="Fulfillment"]:checked');
    if (picked) hide = picked.value.indexOf('Pickup') === 0;
    else if (document.querySelector('[data-mariposa-pickup-only]')) hide = true;
    buttons.forEach(function (b) { b.style.display = hide ? 'none' : ''; });
  }

  /* B2B mixed-cart state (audit #3): the click gate hard-blocks, and this keeps
     the checkout button visually disabled + the banner shown while a mail line
     is in a wholesale cart — re-checked live as lines are removed/added. */
  function applyB2bConflictState() {
    if (!cfg || !cfg.b2b) return;
    var blocked = b2bBlocked();
    document.querySelectorAll('button[name="checkout"]').forEach(function (b) {
      b.classList.toggle('ms-checkout-blocked', blocked);
      if (blocked) b.setAttribute('aria-disabled', 'true');
      else if (b.getAttribute('aria-disabled') === 'true') b.removeAttribute('aria-disabled');
    });
    var banner = el('[data-ms-b2b-conflict]');
    if (banner) banner.hidden = !cfg.b2bMailConflict;
    /* 15+ cases, and a Will Call-only item on a Delivery order (2026-10-10). */
    var cases = el('[data-ms-b2b-cases]');
    if (cases) cases.hidden = !cfg.b2bCaseLimit;
    var collect = el('[data-ms-b2b-collect-ship]');
    if (collect) collect.hidden = !cfg.b2bCollectShip;
  }

  /* Bakeshop-only lines (Patti 2026-08-15): sold at the counter, never
     pre-ordered. Mirrors the B2B gate — visual disable + banner + click block. */
  function applyBakeshopConflictState() {
    if (!cfg) return;
    var conflict = !!cfg.bakeshopConflict;
    var blocked = conflict || b2bBlocked();
    document.querySelectorAll('button[name="checkout"]').forEach(function (b) {
      b.classList.toggle('ms-checkout-blocked', blocked);
      if (blocked) b.setAttribute('aria-disabled', 'true');
      else if (b.getAttribute('aria-disabled') === 'true') b.removeAttribute('aria-disabled');
    });
    var banner = el('[data-ms-bakeshop-conflict]');
    if (banner) banner.hidden = !conflict;
  }

  /* Whether a line is bakeshop-only depends on a product metafield, which
     /cart.js does not carry — so re-read the server-rendered cart, which
     recomputes the flag and the item names in the banner. Dawn re-renders its
     own cart sections on a line removal but not this one, so without this the
     banner and the disabled checkout button survive removing the item they
     complain about, leaving the buyer stuck. */
  function recheckBakeshopConflict() {
    if (!cfg) return Promise.resolve();
    return fetch('/cart', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshCfgEl = doc.querySelector('[data-mariposa-schedule-config]');
        if (!freshCfgEl) return;
        var fresh;
        try { fresh = JSON.parse(freshCfgEl.textContent); } catch (e) { return; }
        cfg.bakeshopConflict = !!fresh.bakeshopConflict;
        // refresh the banner copy too — it names the offending items
        var freshBanner = doc.querySelector('[data-ms-bakeshop-conflict]');
        var liveBanner = el('[data-ms-bakeshop-conflict]');
        if (freshBanner && liveBanner) liveBanner.innerHTML = freshBanner.innerHTML;
        applyBakeshopConflictState();
        setTimeout(applyBakeshopConflictState, 400);
      })
      .catch(function () {});
  }

  /* A per-product lead time (mariposa.lead_time_days) sets a floor on the
     earliest pickup date, computed server-side into the schedule config's
     leadDays. When the lead-time item is REMOVED, /cart.js does not carry the
     new figure, so the calendar kept the pushed-out floor until a full reload
     (pickup browser test, 2026-08-24). Re-read the server-rendered cart and, if
     leadDays changed, rebuild the date list and calendar. */
  function recheckLeadTime() {
    if (!cfg || !el('[data-ms-date]')) return Promise.resolve();
    return fetch('/cart', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshEl = doc.querySelector('[data-mariposa-schedule-config]');
        if (!freshEl) return;
        var fresh;
        try { fresh = JSON.parse(freshEl.textContent); } catch (e) { return; }
        if (fresh.leadDays === cfg.leadDays && fresh.minDate === cfg.minDate) return;
        cfg.leadDays = fresh.leadDays;
        cfg.minDate = fresh.minDate;
        cfg.today = fresh.today;
        var dateSel = el('[data-ms-date]');
        var keep = dateSel ? dateSel.value : '';
        populateDates(keep);
        if (keep && dateSel.value !== keep) populateTimes('');
        calMonth = null;
        renderCalendar();
      })
      .catch(function () {});
  }

  /* Whether a wholesale cart holds something that does not belong depends on a
     product TAG (avail:wholesale), which /cart.js does not carry. It used to key
     on requires_shipping, which only worked while case products were
     non-shipping; once they became shippable every wholesale cart disabled its
     own checkout button. Re-read the server-rendered cart, which recomputes the
     flag — the same approach the bakeshop conflict already uses. */
  function recheckB2bConflict() {
    if (!cfg || !cfg.b2b) return Promise.resolve();
    return fetch('/cart', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshCfgEl = doc.querySelector('[data-mariposa-schedule-config]');
        if (!freshCfgEl) return;
        var fresh;
        try { fresh = JSON.parse(freshCfgEl.textContent); } catch (e) { return; }
        cfg.b2bMailConflict = !!fresh.b2bMailConflict;
        applyB2bConflictState();
        setTimeout(applyB2bConflictState, 400);
      })
      .catch(function () {});
  }

  /* Writing the wholesale fulfilment choice (audit finding 29). The Function has
     always read `ws_fulfillment`; nothing wrote it. Reload after writing, because
     the choice changes which scheduler blocks the server renders. */
  function bindWsFulfilment() {
    document.addEventListener('change', function (e) {
      var r = e.target.closest && e.target.closest('[data-ms-ws-choice]');
      if (!r || !r.checked) return;
      /* Busy from the click to the reload, either direction: Checkout, quantities and
         the radios wait (QA-wholesale issue 1).
         Switching to Delivery: write ws_fulfillment=ship FIRST, then strip the Will
         Call line labels (awaited), then reload. That order means a strip interrupted
         by leaving the page is finished by the next page load (init strips any ship
         cart, under the same busy guard, with the saved pre-strip totals) — it used to
         strip first, so an interrupted strip left a Will Call cart with half-rebuilt
         lines that nothing came back to. */
      wsBusy(true);
      var reloading = false;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { ws_fulfillment: r.value } })
      }).then(function (response) {
        if(!response.ok)throw new Error('Could not save your fulfillment choice. Please try again.');
        return response.json();
      }).then(function(cart){
        if(!cart.attributes || cart.attributes.ws_fulfillment!==r.value)throw new Error('Your fulfillment choice was not saved. Please try again.');
        return r.value === 'ship' ? clearWillCallStamps(cart) : true;
      }).then(function (ok) {
        if (ok === false) throw new Error('We could not update every item in your cart. Please check your quantities before checking out.');
        reloading = true;
        window.location.reload();
      }).catch(function(error){
        var notice=document.querySelector('[data-ms-ws-error]');
        if(!notice){notice=document.createElement('p');notice.setAttribute('data-ms-ws-error','');notice.setAttribute('role','alert');r.parentNode.parentNode.appendChild(notice);}
        notice.textContent=error.message || 'Could not save your fulfillment choice. Please try again.';
        r.checked=false;
      }).then(function () { if (!reloading) wsBusy(false); });
    });
  }

  function markInvalid() {
    var block = el('[data-ms-block="pickup"]');
    if (block) {
      block.classList.add('ms-invalid');
      block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* MNR-7 — every cart write reads its status AND verifies its effect.
     A `fetch()` to /cart/update.js followed by a bare `.catch(function(){})` is
     not error handling: a 4xx or 5xx does not reject a fetch promise, so that
     catch never fires and a refused write is discarded in silence. This store
     answers 429 on cart mutations routinely (/cart/clear.js needed 23 retries in
     one measured run on 2026-09-06), so refusals are the normal case. That is
     what produced A9 — 17 of 22 scheduled orders carrying a wrong or missing line
     stamp, including Eileen's own #1031.
     `want` is the attribute map being written; every key in it is checked back
     off a fresh /cart.js read. Resolves true when the server agrees, false when
     it still does not after three tries. */
  function writeCartAttributes(want, attempt) {
    attempt = attempt || 0;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: want })
    })
      .then(function (r) { return r.ok ? null : 'refused'; })
      .catch(function () { return 'refused'; })
      .then(function (refused) {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var a = (cart && cart.attributes) || {};
            var wrong = Object.keys(want).some(function (k) { return (a[k] || '') !== (want[k] || ''); });
            if (!wrong) return true;
            if (attempt >= 3) return false;
            return new Promise(function (res) { setTimeout(res, refused ? 500 * (attempt + 1) : 0); })
              .then(function () { return writeCartAttributes(want, attempt + 1); });
          });
      })
      .catch(function () { return false; });
  }

  /* A10: a retail pickup_location (Oakland / San Francisco) left by a prior
     retail session survives into a B2B cart. B2B sign-in is not a path write, so
     mariposa-path.js never clears it, and the Function's ws_willcall branch —
     validLocations ["Will Call"] — then rejects the stale value; the hydration
     guard hides it from view but the attribute stays until re-picked. Clear the
     retail schedule attributes once when a B2B session carries a location that
     is not valid for its channel, so the Will Call picker starts clean. Guarded
     on `!cfg.locations[loc]`, so a buyer who already picked "Will Call" (a valid
     location) is never scrubbed. Also clears shopping_mode, which the Function
     ignores for B2B (A11) but which confuses her reading of the order.
     NOTE: mechanism is code-supported but not yet reproduced end-to-end — run
     her retail-then-sign-in sequence in a browser to confirm before trusting.

     STATUS IS READ AND THE CLEAR IS VERIFIED (2026-09-06), for the same reason
     the A9 stamp above now is. This was one fire-and-forget /cart/update.js whose
     response status was never looked at, and this store answers 429 on cart
     mutations often enough that /cart/clear.js needed 23 retries in one measured
     run. A refused clear leaves exactly the stale `pickup_location=Oakland` this
     function exists to remove, and leaves it invisibly: the hydration guard
     hides the value, so nobody sees it until the Function rejects the order.
     A silent failure inside the fix for a client-reported defect reads, from the
     outside, exactly like the defect never having been fixed. */
  function scrubStaleB2bLocation() {
    return writeCartAttributes({ pickup_date: '', pickup_time: '', pickup_location: '', 'pick-up-location': '', delivery_date: '', shopping_mode: '' });
  }

  /* Parse config + (re)render everything. Safe to run again — used at load and
     on bfcache restores (pageshow), where the browser resurrects a stale DOM
     whose calendar may offer dates today's rules forbid (test report bug 4).
     Until this runs, the calendar shows only the server-rendered "Loading
     available dates…" placeholder — nothing selectable pre-hydration. */
  function hydrate(liveAttrs) {
    var cfgEl = document.querySelector('[data-mariposa-schedule-config]');
    cfg = null;
    if (cfgEl) {
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = null; }
    }
    /* bfcache restore: the config JSON is the cart as it was when the page FIRST loaded,
       so a date chosen afterwards came back empty after Back from checkout and the next
       Checkout tap was refused (R-final finding 2). Use the cart's current attributes. */
    if (cfg && liveAttrs) {
      cfg.attributes = cfg.attributes || {};
      ['pickup_location', 'pickup_date', 'pickup_time', 'ship_date'].forEach(function (k) {
        cfg.attributes[k] = liveAttrs[k] == null ? null : liveAttrs[k];
      });
    }
    if (cfg) {
      var a = cfg.attributes || {};
      var locSel = el('[data-ms-location]');
      if (locSel && a.pickup_location && cfg.locations[a.pickup_location]) locSel.value = a.pickup_location;
      // A10: stale retail location carried into a B2B cart — scrub it (see above)
      if (cfg.b2b && a.pickup_location && !(cfg.locations && cfg.locations[a.pickup_location])) scrubStaleB2bLocation();
      populateDates(a.pickup_date || '');
      populateTimes(a.pickup_time || '');
      calMonth = null;
      renderCalendar();
      renderFreshNote();
      updateBuyNowVisibility();
      /* D-024: a default applied at hydrate has not been through the change
         handler, which is what writes the cart attributes. Persist it when the
         select now holds a time the cart does not, and only when this cart
         actually has pickup lines and a date+location are already chosen —
         so an empty or non-pickup cart is never written to. */
      if (cfg.hasPickupLines && pickupComplete() && (el('[data-ms-time]') || {}).value && (el('[data-ms-time]') || {}).value !== a.pickup_time) writeAttributes();
      // stale clearing when no pickup lines remain
      if (!cfg.hasPickupLines && (a.pickup_date || a.pickup_location || a.pickup_time)) writeAttributes();
      applyB2bConflictState();
      applyBakeshopConflictState();
    }
    updateDrawerSummary();
    /* First load, a section re-render and a bfcache restore all come through here. */
    xpConfirm();
    drawerPaint();
  }

  /* Gift note — its own writer. Safe alongside writeAttributes() because
     /cart/update.js merges: verified that a gift_note-only write leaves the 5
     scheduler attributes intact and that the clearing branch leaves gift_note
     intact. Bound once; hydrate() only re-syncs the value. */
  /* Bound per NODE, not once per page: the drawer copy (snippets/mariposa-gift-note.liquid)
     is replaced every time Dawn re-renders the drawer, and a boolean left the new
     textarea with no listeners. */
  var giftBoundTa = null;
  function initGiftNote() {
    var ta = document.querySelector('[data-ms-gift-note]');
    if (!ta || ta === giftBoundTa) return;
    giftBoundTa = ta;
    var status = document.querySelector('[data-ms-gift-status]');
    var count = document.querySelector('[data-ms-gift-count]');
    var timer = null;
    var lastSaved = ta.value;

    function paint() { if (count) count.textContent = ta.value.length + ' / ' + (ta.maxLength > 0 ? ta.maxLength : 300); }
    function save() {
      if (ta.value === lastSaved) return;
      var v = ta.value;
      if (status) status.textContent = 'Saving';
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { gift_note: v } })
      }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        lastSaved = v;
        xpConfirm();
        if (status) {
          status.textContent = v ? 'Saved' : 'Removed';
          setTimeout(function () {
            if (status && (status.textContent === 'Saved' || status.textContent === 'Removed')) status.textContent = '';
          }, 2000);
        }
      }).catch(function () {
        if (status) status.textContent = 'Not saved — check your connection';
      });
    }

    ta.addEventListener('input', function () { xpHide(); paint(); clearTimeout(timer); timer = setTimeout(save, 700); });
    ta.addEventListener('blur', function () { clearTimeout(timer); save(); });
    paint();
  }

  /* Emptying the cart does NOT clear cart attributes — verified 2026-08-16:
     after /cart/clear.js the gift_note survived and reattached itself to a
     freshly rebuilt cart. Left alone, a customer who starts over would ship
     their previous message on an unrelated order. Mirrors the scheduler's own
     stale-attribute clearing. */
  function clearStaleGiftNote() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var pickup = document.documentElement.getAttribute('data-mariposa-path') === 'pickup';
      if (c.item_count !== 0 && !pickup) return;
      if (!c.attributes || !c.attributes.gift_note) return;
      /* Verified, not fired and forgotten (MNR-7). This only acts while the cart
         is empty, so once the shopper adds a line it never runs again — and a
         silently refused clear then lets the stale note ride onto that order,
         which is the harm the comment above describes. */
      writeCartAttributes({ gift_note: '' }).then(function (ok) {
        if (!ok) return;
        var ta = document.querySelector('[data-ms-gift-note]');
        if (ta) ta.value = '';
      });
    }).catch(function () {});
  }

  /* Emptying the cart must also drop the schedule slot. pickup_date/pickup_time
     and the delivery_date the checkout Function/Chrysalis read survive
     /cart/clear.js and Dawn's remove-to-empty (QA 2026-09: a stale 09/14 10:00am
     rode onto the next line and let a checkout through). Mirrors
     clearStaleGiftNote — runs on every cartUpdate, cfg-independent, and only
     writes when the cart is empty AND a schedule attr is still set (so it adds
     no write on normal updates and cannot loop). Keep this list in sync with
     SCHEDULE_ATTRS in mariposa-path.js. */
  var STALE_SCHEDULE = ['pickup_date', 'pickup_time', 'pickup_location', 'pick-up-location', 'delivery_date', 'ship_date', 'ship_date_human', 'arrival_estimate', 'ws_fulfillment'];
  function clearStaleSchedule() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      if (c.item_count !== 0 || !c.attributes) return;
      var dirty = STALE_SCHEDULE.some(function (k) { return c.attributes[k]; });
      if (!dirty) return;
      var a = {};
      STALE_SCHEDULE.forEach(function (k) { a[k] = ''; });
      // Verified, not fired and forgotten (MNR-7) — same reason as the gift note
      // above: once a line is added this stops acting, so a silently refused
      // clear lets the stale slot ride onto the next order.
      writeCartAttributes(a);
    }).catch(function () {});
  }

  /* Group-order recipients ride the cart as gift_plan (JSON) + a human-readable
     "Gift recipients" attribute the fulfilment team reads.

     2026-09-24 — THIS NO LONGER WIPES A PLAN THAT STOPPED MATCHING THE CART.
     It used to clear gift_plan whenever the planned quantity differed from the
     cart's, which is what produced order #1081: Eileen saved three recipients,
     added an item, and the plan vanished silently while the GROUP-SHIP-5 fee
     line stayed — she paid $80 of Multiple Recipient Shipping and every item
     shipped to her. The plan now stays at the cart level (her See's Candies
     ask): assets/mariposa-group-plan.js compares it with the cart, shows what
     is not yet assigned, and sends checkout to the recipients page; the
     schedule-validation Function refuses checkout until the plan covers the
     cart. The ONLY case left here is an empty cart — nothing to send, so the
     group order is over. The shipping line, if any is left, is then an orphan
     and cart-drawer.liquid's guard removes it. */
  function clearStaleGiftPlan() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var attrs = c.attributes || {};
      if (!attrs.gift_plan && !attrs['Gift recipients'] && !attrs.group_shipping) return;
      var gifts = (c.items || []).filter(function (i) {
        return !(i.handle === 'group-shipping' || (i.sku || '') === 'GROUP-SHIP-5');
      });
      if (gifts.length) return;
      /* All three together — they are written together and every partial clear
         in this feature's history left one behind (group_shipping "10.00" on an
         empty cart, 2026-09-05). */
      writeCartAttributes({ gift_plan: '', 'Gift recipients': '', group_shipping: '' });   // verified, not fired and forgotten (MNR-7)
    }).catch(function () {});
  }

  /* ORPHANED PANEL (QA 2026-09-04, both retail paths; a fresh load was always
     clean). The scheduler is its own section in templates/cart.json — a
     deliberate choice, so Dawn's cart re-render cannot clobber a half-made date
     selection. The cost is that Dawn's re-render never refreshes it either: its
     cart fetches ask only for cart-items / cart-drawer / cart-icon-bubble. So
     removing the last line in-page left a full pickup calendar standing over an
     empty cart, and removing every SHIPPING line left the mail ship block up.
     Reload fixed it, which is exactly the signature of a section nobody
     re-rendered.

     Re-rendering on EVERY cartUpdate would undo the reason the section is
     separate, so it re-renders only when the cart changes shape in a way the
     section's own Liquid branches on: whether the cart is empty, whether it
     holds a non-shipping (pickup) line, and whether it holds a shipping line.
     Gift-card lines are skipped, matching the `continue` at the top of the
     section's loop. The bakeshop/B2B conflict banners are NOT in the signature —
     they have their own recheck* calls on the same event, and their inputs
     (product tags/metafields) are not in /cart.js.

     A half-made selection is carried across the swap by hand: only a COMPLETE
     location+date+time triple is ever written to cart attributes
     (writeAttributes), so the re-rendered markup would otherwise come back
     blank for a shopper who had picked a date but not yet a time. */
  var lastShape = null;
  function cartShape(c) {
    if (!c) return null;
    var pickup = false, ship = false;
    (c.items || []).forEach(function (i) {
      if (i.gift_card) return;
      if (i.requires_shipping) ship = true;
      else pickup = true;
    });
    return (c.item_count === 0 ? 'empty' : 'items') + '|' + pickup + '|' + ship;
  }

  /* Remember the wrapper id once we have seen it. The section id is
     template-prefixed (shopify-section-template--<n>__mariposa-scheduler), so
     it cannot be hardcoded, and it is normally found via the config <script>
     inside the section — but a render that emits no config element would then
     leave us unable to find the section ever again. */
  var sectionWrapId = null;
  function sectionWrap() {
    if (sectionWrapId) {
      var known = document.getElementById(sectionWrapId);
      if (known) return known;
    }
    var cfgEl = document.querySelector('[data-mariposa-schedule-config]');
    var wrap = cfgEl && cfgEl.closest ? cfgEl.closest('[id^="shopify-section-"]') : null;
    if (!wrap) {
      var all = document.querySelectorAll('[id^="shopify-section-"][id$="mariposa-scheduler"]');
      wrap = all.length ? all[0] : null;
    }
    if (wrap) sectionWrapId = wrap.id;
    return wrap;
  }

  function rerenderSection() {
    var wrap = sectionWrap();
    if (!wrap) return Promise.resolve();
    var sectionId = wrap.id.replace('shopify-section-', '');

    var locEl = el('[data-ms-location]'), dateEl = el('[data-ms-date]'), timeEl = el('[data-ms-time]');
    var noteEl = document.querySelector('[data-ms-gift-note]');
    var snap = {
      loc: locEl ? locEl.value : '',
      date: dateEl ? dateEl.value : '',
      time: timeEl ? timeEl.value : '',
      note: noteEl ? noteEl.value : null,
      noteFocused: !!(noteEl && document.activeElement === noteEl),
      noteCaret: noteEl ? noteEl.selectionStart : 0
    };

    /* POST, not GET /cart?section_id= : the GET is cacheable and was measured
       returning a pre-change cart snapshot (see stampLines). */
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: [sectionId], sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        var html = state && state.sections && state.sections[sectionId];
        if (typeof html !== 'string') return;
        var fresh = new DOMParser().parseFromString(html, 'text/html');
        var src = fresh.getElementById(wrap.id) || fresh.body.firstElementChild;
        if (!src) return;
        wrap.innerHTML = src.innerHTML;

        /* The gift-note listeners died with the old textarea; giftBound is what
           stops initGiftNote re-binding, so drop it before re-initialising. */
        giftBoundTa = null;
        hydrate();
        initGiftNote();
        /* The delivery card lives in this section since 2026-10-09; a fresh copy is
           empty until filled. */
        fillShipEstimate();

        var nLoc = el('[data-ms-location]'), nDate = el('[data-ms-date]'), nTime = el('[data-ms-time]');
        if (nLoc && snap.loc) {
          nLoc.value = snap.loc;
          if (nLoc.value !== snap.loc) nLoc.value = '';
        }
        if (snap.date && nDate) {
          populateDates(snap.date);
          populateTimes(snap.time || '');
          calMonth = null;
          renderCalendar();
          renderFreshNote();
        }
        var nNote = document.querySelector('[data-ms-gift-note]');
        if (nNote && snap.note !== null && snap.note !== nNote.value) {
          nNote.value = snap.note;
          if (snap.noteFocused) {
            nNote.focus();
            try { nNote.setSelectionRange(snap.noteCaret, snap.noteCaret); } catch (e) {}
          }
        }
      })
      .catch(function () {});
  }

  function resyncSection() {
    if (!sectionWrap()) return Promise.resolve();
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var shape = cartShape(c);
        if (shape === lastShape) return;
        lastShape = shape;
        return rerenderSection();
      })
      .catch(function () {});
  }

  /* Dawn rebuilds #CartDrawer's markup on every add / quantity change, and publishes
     cartUpdate BEFORE it paints (product-form.js), so the cartUpdate pass fills nodes
     that are about to be thrown away. Watch the drawer and fill/bind the fresh copy.
     data-ms-filled marks a filled estimate, so the observer never loops on its own
     text writes. */
  /* Cart page: the Checkout button reads "CHECKOUT — $99.95" (Tom 2026-10-09). The
     button is outside the .js-contents block Dawn re-renders, so copy the fresh total
     from it. Dawn publishes cartUpdate after painting (cart.js), so it is current. */
  function syncCheckoutTotal() {
    var src = document.querySelector('#main-cart-footer [data-msd-total]');
    var dst = document.querySelector('#main-cart-footer [data-msd-checkout-total]');
    if (src && dst) dst.textContent = src.textContent;
  }
  /* Dawn repaints the footer only when the change came through its own cart-items
     (source 'cart-items'). Any other cartUpdate on /cart — the cart changed in another
     tab or at checkout and mariposa-group-plan.js publishes 'mariposa-return' on focus —
     re-renders the lines only, so the estimated total, the Checkout label and the
     minimum-quantity state kept the old cart until a reload (V-total-fix.md). Re-render
     the footer's .js-contents the way xpServerFlags() does (POST, uncacheable), then
     copy. footSeq drops a response that a newer cartUpdate has overtaken. */
  var footSeq = 0;
  function refreshFooterTotals(evt) {
    var seq = ++footSeq;
    var foot = document.getElementById('main-cart-footer');
    if (!foot || !foot.dataset.id || (evt && evt.source === 'cart-items')) return Promise.resolve();
    var sid = foot.dataset.id;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: [sid], sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (st) {
        if (seq !== footSeq) return;
        var h = st && st.sections && st.sections[sid];
        if (typeof h !== 'string') return;
        var src = new DOMParser().parseFromString(h, 'text/html').querySelector('.js-contents');
        var dst = foot.querySelector('.js-contents');
        if (!src || !dst) return;
        dst.innerHTML = src.innerHTML;
        foot.classList.toggle('is-empty', st.item_count === 0);
        syncCheckoutTotal();
        syncMinShort();
      })
      .catch(function () {});
  }
  function watchDrawer() {
    var d = document.querySelector('cart-drawer');
    if (!d || typeof MutationObserver === 'undefined') return;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        if (!cfg && d.querySelector('[data-ms-ship-estimate]:not([data-ms-filled])')) fillShipEstimate();
        initGiftNote();
        drawerPaint();
      }, 0);
    }).observe(d, { childList: true, subtree: true });
  }

  function init() {
    hydrate();
    /* Strip stale Will Call labels on any wholesale page whose cart is in
       Delivery mode, INDEPENDENT of the scheduler panel. restampFromCart below
       only runs when cfg (the panel) or a drawer summary is present, and a
       wholesale SHIP cart may render neither — so the ship-branch clear was
       being skipped and the label survived (2026-09-16). Gated on the wholesale
       path so it costs one /cart.js only on wholesale pages, and clearWillCallStamps
       itself no-ops when nothing carries the stamp. */
    var wsCheckP = Promise.resolve();
    if ((document.documentElement.getAttribute('data-mariposa-path') || '') === 'wholesale') {
      wsCheckP = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var live = (c && c.attributes && c.attributes.ws_fulfillment) || '';
          /* A reload while the Delivery choice was still being saved renders the page from
             the cart as it was (Will Call radios and calendar), then the save lands and the
             strip finishes: the cart is Delivery but the page still shows Will Call, and the
             first Checkout tap only reloaded (LIVE-smoke 4b). Once the cart is consistent,
             reload once so the page matches it. Checkout stays closed meanwhile (wait gate). */
          var reconcile = function () {
            // What the SERVER rendered (defaultChecked), not a radio the buyer tapped since load.
            var shown = null;
            Array.prototype.forEach.call(document.querySelectorAll('[data-ms-ws-choice]'), function (r) { if (r.defaultChecked) shown = r; });
            if (!shown || !live || shown.value === live) return;
            var last = 0;
            try { last = +sessionStorage.getItem('ms-ws-reconcile') || 0; } catch (e) {}
            if (Date.now() - last < 15000) return; // never loop
            try { sessionStorage.setItem('ms-ws-reconcile', String(Date.now())); } catch (e) {}
            window.location.reload();
            return new Promise(function () {}); // keep the wait gate closed until the reload
          };
          if (live !== 'ship') return reconcile();
          var dirty = (c.items || []).some(wcStamped);
          if (dirty) wsBusy(true);
          return clearWillCallStamps(c).then(function (ok) {
            if (dirty) wsBusy(false);
            // R-fixbatch R4: this failure used to be silent and Checkout re-enabled.
            if (ok === false) { showWsStripError(); return; }
            return reconcile();
          });
        })
        .catch(function () {});
    }
    /* Record the shape the server just rendered, so the first cartUpdate does
       not re-render a section that is already correct. */
    if (sectionWrap()) {
      fetch('/cart.js').then(function (r) { return r.json(); })
        .then(function (c) { lastShape = cartShape(c); })
        .catch(function () {});
    }
    /* Stamp anything that arrived without a note. Quick Order adds through a
       bare /cart/add.js and publishes no cartUpdate event, so a line added there
       reached the cart unstamped and stayed that way even after a reload
       (browser retest, 2026-08-22). Catch it when the cart page loads, which
       covers every route in, not just the ones that fire an event. */
    /* Only when there is something to stamp: cfg means the cart page's
       scheduler panel, and the drawer summary is rendered on every page whose
       cart holds a collect line. Without that guard this would cost one
       /cart.js on every page view of the store. */
    var restampP = Promise.resolve();
    if (cfg || document.querySelector('[data-mariposa-drawer-summary]')) restampP = restampFromCart().then(function () { xpConfirm(); });
    syncMinShort();
    /* Wholesale Checkout opens only once the page-load check, strip and restamp have
       settled (see wsWaitRelease); a check that never answers opens it after
       WS_WAIT_MS with the phone line shown. */
    if (wsWaitBtn()) {
      /* A strip that is still making progress is not a stuck check: a 9-line cart
         resumed after a reload took ~18 s, and the phone line at 10 s told the buyer
         something was wrong when nothing was (R-fixbatch2 N3). Keep waiting while a
         strip runs, up to 30 s in all. */
      var wsWaitStart = Date.now();
      var wsWaitTimer;
      var wsWaitTick = function () {
        if (wcStripP && Date.now() - wsWaitStart < 30000) { wsWaitTimer = setTimeout(wsWaitTick, 2000); return; }
        wsWaitRelease(true);
      };
      wsWaitTimer = setTimeout(wsWaitTick, WS_WAIT_MS);
      /* Only now does this script own the call-us line: the inline fallback timer in
         main-cart-footer.liquid stands down. Set any earlier and an error in init() above
         left no timer at all (R-final round 2). */
      window.MariposaSchedulerReady = true;
      Promise.all([wsCheckP, restampP.catch(function () {})]).then(function () {
        clearTimeout(wsWaitTimer);
        wsWaitRelease(false);
      });
    }
    bindWsFulfilment();
    fillShipEstimate();
    initGiftNote();
    watchDrawer();
    clearStaleGiftNote();
    clearStaleSchedule();
    clearStaleGiftPlan();
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (c) { hydrate((c && c.attributes) || {}); }, function () { hydrate(); });
    });
    /* Express gate and drawer check against the clock: every 30 s and when the tab comes
       back, because a cutoff can pass while the page sits open. */
    if (xpWrap() || document.querySelector('cart-drawer')) {
      setInterval(function () { xpTick(); drawerPaint(); }, 30000);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) return;
        drawerPaint();
        var w = xpWrap();
        if (!w) return;
        if (xpNeedsSchedule(w) && !scheduleStillValid()) xpHide();
        else xpConfirm();
      });
    }
    /* Hide the express buttons the moment a quantity, a removal or the order note starts
       to change, before Dawn's request leaves; cartUpdate / the save confirm again. */
    var xpNoteTimer = null;
    document.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || !t.closest || !xpWrap()) return;
      if (t.closest('cart-items')) xpHide();
      if (t.id === 'Cart-note') {
        xpHide();
        clearTimeout(xpNoteTimer);
        xpNoteTimer = setTimeout(function () { xpConfirm(); }, 1200);
      }
    }, true);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('cart-items')) xpHide();
    }, true);
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest || !xpWrap()) return;
      if (e.target.closest('cart-items cart-remove-button, cart-items quantity-input button')) xpHide();
    }, true);
    // keep the B2B mixed-cart state live as lines are removed/added.
    // NOTE: Dawn's PUB_SUB_EVENTS is a top-level const (not a window property) —
    // reference it bare, with the literal event name as fallback.
    if (typeof window.subscribe === 'function') {
      var cartUpdateEvt = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
      window.subscribe(cartUpdateEvt, function (evt) {
        /* cartUpdate comes only from the buyer's own cart actions (quantities are locked
           while a strip runs). After a failed strip the buyer was asked to check their
           quantities, so what they set now is the target: drop the saved pre-strip totals,
           or the next strip would rebuild the cart back to them. */
        if (!wcStripP) wcSave(null, null);
        xpHide();
        syncCheckoutTotal();
        syncMinShort();
        var pending = [
          refreshFooterTotals(evt),
          recheckB2bConflict(),
          recheckB2bCart().catch(function () {}),
          recheckBakeshopConflict(),
          recheckLeadTime()
        ];
        fillShipEstimate();
        clearStaleGiftNote();
        clearStaleSchedule();
        clearStaleGiftPlan();
        pending.push(restampFromCart());
        /* Dawn replaces the drawer's markup on every cart change, so the merged
           cart paragraph comes back with its {zip} token un-substituted. */
        if (window.MariposaShip && window.MariposaShip.applyCartNote) window.MariposaShip.applyCartNote();
        // drop the panel (or the ship block) when the cart no longer has
        // anything for it to schedule — see rerenderSection above.
        pending.push(resyncSection());
        Promise.all(pending).then(function () { xpConfirm(); }, function () { xpConfirm(); });
      });
    }

    /* All interactions are delegated at document level so they survive any
       section/DOM replacement (the first-click-swallowed arrow, bug 7, was a
       listener lost with its node). */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('input[data-option-name="Fulfillment"]')) {
        // PDP dead-end guard: hide Buy-it-now when the selected variant is pickup
        setTimeout(updateBuyNowVisibility, 50);
        return;
      }
      if (!cfg) return;
      if (t.matches('[data-ms-location]') || t.matches('[data-ms-date]') || t.matches('[data-ms-time]')) xpHide();
      if (t.matches('[data-ms-location]')) {
        populateDates('');
        populateTimes('');
        calMonth = null;
        renderCalendar();
      }
      if (t.matches('[data-ms-date]')) {
        /* Hours are per weekday now, so the slot list depends on the date. */
        var keepTime = (el('[data-ms-time]') || {}).value || '';
        populateTimes(keepTime);
        renderFreshNote();
      }
      if (t.matches('[data-ms-location]') || t.matches('[data-ms-date]') || t.matches('[data-ms-time]')) {
        var block = el('[data-ms-block="pickup"]');
        if (block && pickupComplete()) block.classList.remove('ms-invalid');
        /* Wholesale Will Call: the new date/time is being stamped onto every line; hold
           Checkout and quantities until the stamp chain settles (QA-wholesale issue 1). */
        var stampBusy = !!(cfg.b2b && cfg.channel === 'ws_willcall');
        if (stampBusy) wsBusy(true);
        writeAttributes().then(function () {
          if (stampBusy) wsBusy(false);
          xpConfirm();
        }, function () { if (stampBusy) wsBusy(false); });
      }
    });

    document.addEventListener('click', function (e) {
      if (!cfg || !e.target.closest) return;
      var day = e.target.closest('[data-ms-day]');
      if (day) {
        var dateSel = el('[data-ms-date]');
        if (!dateSel) return;
        var wanted = day.getAttribute('data-ms-day');
        if (dateSel.value === wanted) return; // re-click on the selected day is a no-op (bug 8)
        dateSel.value = wanted;
        if (dateSel.value !== wanted) { populateDates(wanted); dateSel.value = wanted; }
        dateSel.dispatchEvent(new Event('change', { bubbles: true }));
        renderCalendar();
        /* The time select now sits BELOW the calendar, which is the only order
           the per-weekday slot list makes sense in — but on a phone that puts it
           off the bottom of the screen at the moment the shopper finishes with
           the date. Bring it into view, and only when it is genuinely off
           screen, so a desktop re-pick does not yank the page around. */
        var timeSel = el('[data-ms-time]');
        if (timeSel && !timeSel.value) {
          var tb = timeSel.getBoundingClientRect();
          if (tb.bottom > window.innerHeight || tb.top < 0) {
            timeSel.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }
      if (e.target.closest('[data-ms-cal-prev]:not([disabled])')) {
        calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1);
        renderCalendar();
      } else if (e.target.closest('[data-ms-cal-next]:not([disabled])')) {
        calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1);
        renderCalendar();
      }
    });

    // checkout gating + attribute re-assert (capture phase catches page & drawer buttons)
    wsGateBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="checkout"]');
      if (!btn) return;
      // Wholesale page-load check still running (wsWaitRelease): the button is disabled, this is belt and braces.
      if (btn.hasAttribute('data-ms-ws-wait')) { e.preventDefault(); e.stopPropagation(); return; }

      // bakeshop-only lines can't be checked out on a scheduled pickup order
      if (cfg && cfg.bakeshopConflict) {
        var bakeshop = el('[data-ms-bakeshop-conflict]');
        if (bakeshop) {
          e.preventDefault();
          e.stopPropagation();
          bakeshop.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }

      /* WHOLESALE (cart page; the B2B drawer CTA is a link to /cart). Retail lines in the
         cart, 15+ cases, a Will Call-only item on a Delivery order, and — for Will Call —
         the stamp on every line, all re-read from the live cart before the submit goes.
         On Grow these are the only checks (2026-10-10, H-wholesale-verify.md). */
      if (cfg && cfg.b2b) {
        if (btn.dataset.msArmed) { delete btn.dataset.msArmed; return; }
        e.preventDefault();
        e.stopPropagation();
        if (b2bBlocked()) { scrollToB2bBanner(); return; }
        b2bCheckout(btn);
        return;
      }

      var onCartPage = !!el('[data-ms-block="pickup"]');
      if (onCartPage && cfg && cfg.hasPickupLines) {
        if (!pickupComplete()) {
          e.preventDefault();
          e.stopPropagation();
          markInvalid();
          return;
        }
        if (!btn.dataset.msArmed) {
          // re-assert all attributes atomically AND re-stamp every line
          // synchronously (A9: a late date-change or line-add left line
          // properties stale — #1031 showed "Wed 9 Sep" on 4 lines while the
          // cart-level date was 10 Sep, and one line unstamped). Sequence
          // writeAttributes (cart-level) then restampFromCart (lines) so both
          // reflect the same currentValues() before the submit proceeds.
          e.preventDefault();
          e.stopPropagation();
          writeAttributes().then(restampFromCart).then(function () {
            btn.dataset.msArmed = '1';
            btn.click();
          });
        } else {
          delete btn.dataset.msArmed;
        }
        return;
      }

      // any other page: the drawer summary is server-rendered with completeness state
      var summary = document.querySelector('[data-mariposa-drawer-summary]');
      if (summary) {
        if (btn.dataset.msArmed) { delete btn.dataset.msArmed; return; }
        e.preventDefault();
        e.stopPropagation();
        // A pickup order needs a date, and the date picker lives on the cart
        // page, not the drawer. Silently blocking the drawer's Check out made it
        // look like a dead button (browser QA 2026-09-03). Send the shopper to
        // the cart to choose the date — the drawer summary already links there.
        if (summary.dataset.complete === 'false' || !summary.dataset.pickupDate) {
          window.location.href = '/cart';
          return;
        }
        if (drawerChecking) return;
        drawerChecking = true;
        /* 2026-10-10: a date that is present is not necessarily still valid. Check it
           against the cart page's rules on the current cart (freshDrawerCheck); invalid,
           or not checkable, goes to /cart. */
        freshDrawerCheck().then(function (valid) {
          if (!valid) { window.location.href = '/cart'; return; }
          /* A9, the drawer half. cart-drawer.liquid computes data-complete from
             cart ATTRIBUTES only — it never looks at a single line's properties —
             so a cart whose date is set reads "complete" even when a line added
             from a PDP or Quick Order carries no note at all. That button then
             went straight to checkout. Stamp from the cart's own attributes and
             wait for it before letting the submit through, the same way the cart
             page does. */
          return stampFromAttributes().then(function () {
            btn.dataset.msArmed = '1';
            btn.click();
          });
        }).then(function () { drawerChecking = false; }, function () { drawerChecking = false; });
        return;
      }

      /* Mail Order from the drawer (no scheduler panel on the page): write the ship
         attributes the cart page would have written, then let the submit go. */
      if (!cfg) {
        var shipVals = drawerShipValues();
        if (shipVals) {
          if (btn.dataset.msArmed) { delete btn.dataset.msArmed; return; }
          e.preventDefault();
          e.stopPropagation();
          ensureShipAttributes(shipVals).then(function () {
            btn.dataset.msArmed = '1';
            btn.click();
          });
        }
      }
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
